import React, { useEffect, useState } from "react";
import Layout from "@app/Layout";
import Canonical from "@shared/head/Canonical";
import Breadcrumbs from "@shared/head/Breadcrumbs";
import "./article.scss";

export const Head = () => (
  <>
    <title>AI SDR Agent Guide 2026: Pricing, ROI &amp; Top Tools | Dashly</title>
    <meta name="description" content="Complete AI SDR agent guide. AI SDR vs AI BDR vs sales agent, inbound vs outbound, pricing models, real customer ROI (568% case), top 12 tools compared, 30-day deployment plan." />
    <Canonical url="https://www.dashly.io/resources/ai-sdr-agent-guide/" />
    <Breadcrumbs list={[
      { name: "Dashly", url: "https://www.dashly.io/" },
      { name: "Resources", url: "https://www.dashly.io/resources/" },
      { name: "AI SDR Agent Guide", url: "https://www.dashly.io/resources/ai-sdr-agent-guide/" },
    ]} />
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
    <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap" rel="stylesheet" />
    <meta property="og:title" content="AI SDR Agent Guide 2026: Pricing, ROI & Top Tools" />
    <meta property="og:description" content="AI SDR vs AI BDR vs sales agent. Inbound vs outbound. Pricing models, real customer ROI (568% case), top 12 tools compared, 30-day deployment plan." />
    <meta property="og:url" content="https://www.dashly.io/resources/ai-sdr-agent-guide/" />
    <meta property="og:type" content="article" />
    <meta property="og:site_name" content="Dashly" />
    <meta property="og:image" content="https://www.dashly.io/dashly-cover.png" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta property="article:published_time" content="2026-03-27" />
    <meta property="article:modified_time" content="2026-05-01" />
    <meta property="article:author" content="https://www.linkedin.com/in/dimitri-ive/" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="AI SDR Agent Guide 2026: Pricing, ROI & Top Tools" />
    <meta name="twitter:description" content="AI SDR vs AI BDR vs sales agent. Inbound vs outbound. Pricing models, real customer ROI (568% case), top 12 tools compared." />
    <meta name="twitter:image" content="https://www.dashly.io/dashly-cover.png" />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: `{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "AI SDR Agent Guide 2026: Pricing, ROI & Top Tools",
  "description": "Complete AI SDR agent guide. AI SDR vs AI BDR vs sales agent, inbound vs outbound, pricing models, real customer ROI (568% case), top 15 tools compared, 30-day deployment plan.",
  "url": "https://www.dashly.io/resources/ai-sdr-agent-guide/",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://www.dashly.io/resources/ai-sdr-agent-guide/"
  },
  "image": [{
    "@type": "ImageObject",
    "url": "https://www.dashly.io/dashly-cover.png",
    "width": 1200,
    "height": 630
  }],
  "author": { "@type": "Person", "name": "Dmitrii Serg", "url": "https://www.linkedin.com/in/dimitri-ive/" },
  "publisher": {
    "@type": "Organization",
    "name": "Dashly",
    "url": "https://www.dashly.io",
    "logo": { "@type": "ImageObject", "url": "https://www.dashly.io/favicon-32x32.png", "width": 32, "height": 32 }
  },
  "datePublished": "2026-03-27",
  "dateModified": "2026-05-01",
  "inLanguage": "en",
  "keywords": ["AI SDR", "AI SDR agent", "what is AI SDR", "AI SDR meaning", "AI SDR definition", "AI sales development representative", "AI BDR", "AI sales agent", "inbound AI SDR", "outbound AI SDR", "AI SDR vs human SDR", "AI SDR pricing", "AI SDR cost", "AI SDR ROI", "best AI SDR tools", "AI inbound qualification", "AI demo booking", "B2B sales automation", "AI lead qualification"]
}` }}
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What is an inbound SDR agent?", "acceptedAnswer": { "@type": "Answer", "text": "An inbound SDR agent is an AI-powered teammate that engages, qualifies, and books meetings with leads who reach out via your website or messaging channels. Unlike a traditional chatbot, it adapts to the conversation, captures structured data, and routes ICP-fit leads to your sales team in real time." } },
    { "@type": "Question", "name": "How long does it take to set up an AI inbound SDR agent?", "acceptedAnswer": { "@type": "Answer", "text": "With a platform like Dashly, initial setup takes 3-7 days: configuring the qualification criteria, uploading the knowledge base, connecting the CRM, and setting up meeting booking. Calibration and optimization continue for the first 2-4 weeks as the agent learns from real interactions." } },
    { "@type": "Question", "name": "What happens when the AI agent cannot answer a question?", "acceptedAnswer": { "@type": "Answer", "text": "Well-configured agents have a defined escalation path: they acknowledge the question, let the lead know they are connecting them with a human, and either transfer the conversation live or schedule a callback. They do not guess or fabricate answers." } },
    { "@type": "Question", "name": "Can the AI agent handle multiple conversations at once?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Unlike a human SDR, the agent handles unlimited concurrent conversations with no degradation in quality. During a product launch or campaign spike, it scales automatically." } },
    { "@type": "Question", "name": "What is the difference between AI SDR and AI BDR?", "acceptedAnswer": { "@type": "Answer", "text": "The two terms are often used interchangeably. The functional split: AI BDR leans outbound (cold email, LinkedIn prospecting). AI SDR spans both inbound (website chat, lead qualification) and outbound. Vendors choose the label that fits their funnel positioning, not a strict industry definition." } },
    { "@type": "Question", "name": "How much does an AI SDR cost in 2026?", "acceptedAnswer": { "@type": "Answer", "text": "Pricing ranges from $24/mo (Dashly entry plan, inbound) to $900/mo per agent (AISDR outbound) to custom enterprise contracts (Conversica, 11x.ai) at $30k+ ARR. Per-resolution pricing (Intercom Fin) bills around $0.99 per resolved conversation. Free tiers exist for piloting. Total year-one cost typically runs 1.2-1.5x listed subscription for inbound agents and 2-3x for autonomous outbound." } },
    { "@type": "Question", "name": "Can an AI SDR work for inbound and outbound at the same time?", "acceptedAnswer": { "@type": "Answer", "text": "Most products specialize. Dashly is inbound-first; AISDR, 11x.ai, and Salesforge are outbound-first. Hybrid platforms (Salesloft, Outreach) cover both at higher per-seat cost. A single AI agent owning both sides on the same CRM record is the next product wave but is not standard yet in 2026." } },
    { "@type": "Question", "name": "What integrations does an AI SDR need?", "acceptedAnswer": { "@type": "Answer", "text": "At minimum: a CRM (HubSpot, Salesforce, Pipedrive), a calendar (Google Calendar, Outlook, Calendly, Chili Piper), and a knowledge base (help center, product docs, sales playbook). Outbound AI SDRs add prospect-data sources (Apollo, ZoomInfo, LinkedIn Sales Navigator) and email-deliverability infrastructure." } },
    { "@type": "Question", "name": "What is the best AI SDR for B2B SaaS?", "acceptedAnswer": { "@type": "Answer", "text": "For inbound-heavy B2B SaaS with active demand-gen and a trial loop, Dashly's four-agent inbound stack ranks first by real customer outcome data (568% ROMI at InfluADS, +536% meetings at Advertising Socials). For outbound-heavy B2B SaaS entering new geographies, AISDR or 11x.ai cover that side. Most teams need both eventually." } },
    { "@type": "Question", "name": "How long does it take to deploy an AI SDR end-to-end?", "acceptedAnswer": { "@type": "Answer", "text": "Inbound web-based agents like Dashly deploy in under 1 day to 1 week for the basic setup, with a 30-day pilot-to-scale rollout. Autonomous outbound agents (Conversica, 11x.ai) with deep CRM and persona setup take 4 to 8 weeks of procurement plus implementation. The two most common rollout failures are skipping the pilot phase and never enabling CRM write-back." } },
    { "@type": "Question", "name": "Will prospects know they are talking to AI?", "acceptedAnswer": { "@type": "Answer", "text": "Transparency practices vary. Some companies disclose upfront. Others configure the agent with a persona name without specifying it is AI. The right choice depends on your brand values and market. What matters most for conversion is that the conversation is useful and relevant. Leads respond positively to agents that help them, regardless of whether they know it is AI." } },
    { "@type": "Question", "name": "How do I measure whether the AI agent is working?", "acceptedAnswer": { "@type": "Answer", "text": "Track funnel metrics: conversation-to-qualification rate, MQL-to-meeting rate, pipeline contribution percentage, and SDR time saved. Compare against your pre-deployment baseline. Most teams see measurable improvements within the first 30 days. Real customer ROI examples: 568% ROMI (InfluADS), 536% meeting increase (Advertising Socials), 60% cost reduction (VoiceFirst)." } }
  ]
}` }}
    />
  </>
);

export default function AIInboundSDRAgents({ location }: { location: any }) {
  // FAQ accordion: which item is open (-1 = all closed)
  const [openFaq, setOpenFaq] = useState<number>(0);

  useEffect(() => {
    type Step =
      | { at: number; action: "show-typing" }
      | { at: number; action: "agent"; text: string }
      | { at: number; action: "user"; text: string; fill?: string; fill2?: string; mql?: boolean }
      | { at: number; action: "crm" }
      | { at: number; action: "restart" };

    const fieldData: Record<string, string> = {
      "qf-team":   "~15 reps",
      "qf-pain":   "Manual qualification",
      "qf-crm":    "Salesforce",
      "qf-budget": "~$2k/mo",
      "qf-intent": "Demo requested",
    };

    let timers: ReturnType<typeof setTimeout>[] = [];
    function clearAll() { timers.forEach(clearTimeout); timers = []; }

    function runAnimation() {
      const msgs = document.getElementById("qdc-messages") as HTMLElement | null;
      const typing = document.getElementById("qdc-typing") as HTMLElement | null;
      const mqlEl = document.getElementById("qdp-mql");
      const crmEl = document.getElementById("qdp-crm");
      if (!msgs || !typing) return;

      // reset
      msgs.innerHTML = "";
      typing.style.display = "none";
      mqlEl?.classList.remove("visible");
      crmEl?.classList.remove("visible");
      Object.keys(fieldData).forEach(id => {
        const valEl = document.getElementById(id);
        const fieldEl = valEl?.closest(".qdp-field");
        if (valEl) valEl.textContent = "—";
        fieldEl?.classList.remove("filled");
      });

      function fillField(id: string) {
        const valEl = document.getElementById(id);
        const fieldEl = valEl?.closest(".qdp-field");
        if (!valEl) return;
        valEl.textContent = fieldData[id] ?? "";
        valEl.classList.add("flash");
        fieldEl?.classList.add("filled");
        setTimeout(() => valEl.classList.remove("flash"), 800);
      }

      function getTime() {
        const d = new Date();
        return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
      }

      function addMsg(from: "agent" | "user", text: string) {
        typing!.style.display = "none";
        const wrap = document.createElement("div");
        wrap.className = `qdc-msg ${from}`;
        const avatarSvg = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="4" y="8" width="16" height="11" rx="3" fill="white"/><rect x="8" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="13" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="10" y="15" width="4" height="1.5" rx="0.75" fill="#5850EC"/><rect x="10.5" y="5" width="3" height="3" rx="1" fill="white"/><rect x="11.5" y="3" width="1" height="2.5" rx="0.5" fill="white"/><rect x="2" y="11" width="2" height="4" rx="1" fill="white"/><rect x="20" y="11" width="2" height="4" rx="1" fill="white"/></svg>`;
        if (from === "agent") {
          wrap.innerHTML = `<div class="qdc-msg-avatar">${avatarSvg}</div><div class="qdc-bubble">${text}</div><div class="qdc-msg-time">${getTime()}</div>`;
        } else {
          wrap.innerHTML = `<div class="qdc-msg-time">${getTime()}</div><div class="qdc-bubble">${text}</div>`;
        }
        msgs!.appendChild(wrap);
        msgs!.scrollTop = msgs!.scrollHeight;
      }

      const sequence: Step[] = [
        { at: 600,   action: "show-typing" },
        { at: 1900,  action: "agent", text: "Hi! I noticed you're on the pricing page — are you evaluating this for your sales team? 🚀" },
        { at: 4400,  action: "user",  text: "Yes, we need a qualification solution for ~15 reps", fill: "qf-team" },
        { at: 5200,  action: "show-typing" },
        { at: 7000,  action: "agent", text: "What's the main pain — slow response, manual qualification, or too many unfit leads in the pipeline?" },
        { at: 9800,  action: "user",  text: "Managers waste hours qualifying cold leads manually", fill: "qf-pain" },
        { at: 10600, action: "show-typing" },
        { at: 12200, action: "agent", text: "Understood. Which CRM are you using — and rough monthly budget?" },
        { at: 15200, action: "user",  text: "Salesforce. Budget around $2,000/month", fill: "qf-crm", fill2: "qf-budget" },
        { at: 16000, action: "show-typing" },
        { at: 17600, action: "agent", text: "Great fit! Want me to book a demo with a specialist? I can show results for similar-sized teams 📅" },
        { at: 20400, action: "user",  text: "Yes — Thursday afternoon works!", fill: "qf-intent", mql: true },
        { at: 21800, action: "crm" },
        { at: 28000, action: "restart" },
      ];

      sequence.forEach(step => {
        const t = setTimeout(() => {
          if (step.action === "show-typing") {
            typing!.style.display = "flex";
            msgs!.scrollTop = msgs!.scrollHeight;
          } else if (step.action === "agent") {
            addMsg("agent", step.text);
          } else if (step.action === "user") {
            addMsg("user", step.text);
            if (step.fill)  fillField(step.fill);
            if (step.fill2) fillField(step.fill2);
            if (step.mql) setTimeout(() => mqlEl?.classList.add("visible"), 500);
          } else if (step.action === "crm") {
            crmEl?.classList.add("visible");
          } else if (step.action === "restart") {
            clearAll();
            runAnimation();
          }
        }, step.at);
        timers.push(t);
      });
    }

    const init = setTimeout(runAnimation, 400);
    timers.push(init);
    return () => clearAll();
  }, []);

  // Nurture canvas drag
  useEffect(() => {
    const canvas = document.getElementById("nurture-canvas") as HTMLElement | null;
    if (!canvas) return;
    const cards = Array.from(canvas.querySelectorAll(".nc-card")) as HTMLElement[];

    let active: HTMLElement | null = null;
    let sx = 0, sy = 0, sl = 0, st = 0;

    const registered: Array<[EventTarget, string, EventListenerOrEventListenerObject, AddEventListenerOptions?]> = [];
    function on(el: EventTarget, type: string, fn: EventListenerOrEventListenerObject, opts?: AddEventListenerOptions) {
      el.addEventListener(type, fn, opts);
      registered.push([el, type, fn, opts]);
    }

    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

    const onMove = (e: MouseEvent) => {
      if (!active) return;
      const newL = clamp(sl + e.clientX - sx, 0, canvas.offsetWidth  - active.offsetWidth);
      const newT = clamp(st + e.clientY - sy, 0, canvas.offsetHeight - active.offsetHeight);
      active.style.left = `${newL}px`;
      active.style.top  = `${newT}px`;
    };
    const onUp = () => {
      active?.classList.remove("nc-dragging");
      active = null;
    };

    on(document, "mousemove", onMove as EventListener);
    on(document, "mouseup",   onUp   as EventListener);

    cards.forEach(card => {
      on(card, "mousedown", ((e: MouseEvent) => {
        active = card; sx = e.clientX; sy = e.clientY;
        sl = parseInt(card.style.left) || 0;
        st = parseInt(card.style.top)  || 0;
        card.classList.add("nc-dragging");
        card.style.zIndex = "30";
        e.preventDefault();
      }) as EventListener);

      on(card, "touchstart", ((e: TouchEvent) => {
        const t = e.touches[0];
        active = card; sx = t.clientX; sy = t.clientY;
        sl = parseInt(card.style.left) || 0;
        st = parseInt(card.style.top)  || 0;
        card.classList.add("nc-dragging");
        e.preventDefault();
      }) as EventListener, { passive: false });

      on(card, "touchmove", ((e: TouchEvent) => {
        if (!active) return;
        const t = e.touches[0];
        const newL = clamp(sl + t.clientX - sx, 0, canvas.offsetWidth  - active.offsetWidth);
        const newT = clamp(st + t.clientY - sy, 0, canvas.offsetHeight - active.offsetHeight);
        active.style.left = `${newL}px`;
        active.style.top  = `${newT}px`;
        e.preventDefault();
      }) as EventListener, { passive: false });

      on(card, "touchend", (() => {
        active?.classList.remove("nc-dragging");
        active = null;
      }) as EventListener);
    });

    return () => {
      registered.forEach(([el, type, fn, opts]) => el.removeEventListener(type, fn, opts));
    };
  }, []);

  // Route demo: two chat animations (not MQL vs MQL)
  useEffect(() => {
    type RStep =
      | { at: number; action: "typing" }
      | { at: number; action: "agent"; text: string }
      | { at: number; action: "user";  text: string }
      | { at: number; action: "outcome" }
      | { at: number; action: "restart" };

    const avatarSvg = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="4" y="8" width="16" height="11" rx="3" fill="white"/><rect x="8" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="13" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="10" y="15" width="4" height="1.5" rx="0.75" fill="#5850EC"/><rect x="10.5" y="5" width="3" height="3" rx="1" fill="white"/><rect x="11.5" y="3" width="1" height="2.5" rx="0.5" fill="white"/><rect x="2" y="11" width="2" height="4" rx="1" fill="white"/><rect x="20" y="11" width="2" height="4" rx="1" fill="white"/></svg>`;

    let allTimers: ReturnType<typeof setTimeout>[] = [];
    function clearAll() { allTimers.forEach(clearTimeout); allTimers = []; }

    function animateChat(prefix: string, steps: RStep[]) {
      const msgs = document.getElementById(`${prefix}-messages`);
      const typ  = document.getElementById(`${prefix}-typing`);
      const out  = document.getElementById(`${prefix}-outcome`);
      if (!msgs || !typ) return;

      msgs.innerHTML = '<div class="rdc-date-sep">Today</div>';
      typ.style.display = "none";
      out?.classList.remove("visible");

      function getTime() {
        const d = new Date();
        return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
      }
      function addMsg(from: "agent" | "user", text: string) {
        typ!.style.display = "none";
        const div = document.createElement("div");
        div.className = `rdc-msg ${from}`;
        if (from === "agent") {
          div.innerHTML = `<div class="rdc-avatar">${avatarSvg}</div><div class="rdc-bubble">${text}</div><div class="rdc-msg-time">${getTime()}</div>`;
        } else {
          div.innerHTML = `<div class="rdc-msg-time">${getTime()}</div><div class="rdc-bubble">${text}</div>`;
        }
        msgs!.appendChild(div);
        msgs!.scrollTop = msgs!.scrollHeight;
      }

      steps.forEach(step => {
        const t = setTimeout(() => {
          if (step.action === "typing")  { typ!.style.display = "flex"; msgs!.scrollTop = msgs!.scrollHeight; }
          else if (step.action === "agent")   { addMsg("agent", step.text); }
          else if (step.action === "user")    { addMsg("user",  step.text); }
          else if (step.action === "outcome") { out?.classList.add("visible"); }
          else if (step.action === "restart") { animateChat(prefix, steps); }
        }, step.at);
        allTimers.push(t);
      });
    }

    const notMql: RStep[] = [
      { at: 800,   action: "typing" },
      { at: 2100,  action: "agent",   text: "Hi! Are you exploring tools for your sales process?" },
      { at: 4800,  action: "user",    text: "Just looking around, not sure I need this yet" },
      { at: 5600,  action: "typing" },
      { at: 7000,  action: "agent",   text: "How many people are on your sales team?" },
      { at: 9600,  action: "user",    text: "Just me for now — solo founder" },
      { at: 10400, action: "typing" },
      { at: 12000, action: "agent",   text: "Got it — our platform works best for teams of 5+. I'll send you some resources to revisit when you're ready to scale 📚" },
      { at: 13600, action: "outcome" },
      { at: 23000, action: "restart" },
    ];

    const mql: RStep[] = [
      { at: 800,   action: "typing" },
      { at: 2100,  action: "agent",   text: "Hi! Are you evaluating this for your sales team?" },
      { at: 4800,  action: "user",    text: "Yes — 15 reps, we need to speed up qualification badly" },
      { at: 5600,  action: "typing" },
      { at: 7000,  action: "agent",   text: "What's your monthly budget for sales tools?" },
      { at: 9200,  action: "user",    text: "$3,000 per month" },
      { at: 10000, action: "typing" },
      { at: 11600, action: "agent",   text: "Great fit! I can book a demo with Chris, our enterprise specialist. When works best?" },
      { at: 14000, action: "user",    text: "Tomorrow at 2pm" },
      { at: 14800, action: "typing" },
      { at: 16200, action: "agent",   text: "Done! ✓ Chris will meet you tomorrow at 2pm. Check your email for the calendar invite." },
      { at: 17600, action: "outcome" },
      { at: 27000, action: "restart" },
    ];

    const t1 = setTimeout(() => animateChat("rc1", notMql), 600);
    const t2 = setTimeout(() => animateChat("rc2", mql),    900);
    allTimers.push(t1, t2);
    return () => clearAll();
  }, []);

  return (
    <Layout location={location} className="new-design">

      {/* ─── HERO ─── */}
      <div className="article-hero">
        <div className="article-hero-inner">
          <span className="tag-line">AI Sales Automation</span>
          <h1>
            Inbound SDR Agents: <em>Complete AI Guide</em> to Qualification, Demo Booking &amp; Nurturing
          </h1>
          <p className="lead">
            Your inbound traffic generates leads around the clock, but your SDR team only works business hours. AI inbound SDR agents close that gap: instant response, intelligent qualification, automatic meeting booking, and follow-up sequences. No added headcount. This guide covers what AI SDR agents do, how they differ from <a href="#what-is-ai-sdr-vs-bdr">human SDRs and AI BDRs</a>, real <a href="#ai-sdr-pricing-models">pricing</a>, deployment, and <a href="https://www.dashly.io/tools/best-ai-sdr-tools/">vendor selection</a>.
          </p>
          <div className="article-meta">
            <div className="author">
              <img className="author-avatar-img" src="/dmitrii-serg-avatar.jpg" alt="Dmitrii Serg" />
              <a href="https://www.linkedin.com/in/dimitri-ive/" target="_blank" rel="noopener noreferrer" className="author-name">Dmitrii Serg</a>
            </div>
            <span className="meta-sep">·</span>
            <span>March 2026</span>
            <span className="meta-sep">·</span>
            <span>12 min read</span>
            <span className="meta-sep">·</span>
            <span>AI Sales</span>
          </div>
        </div>
      </div>

      {/* ─── ARTICLE BODY ─── */}
      <main className="article-content">
        <div className="content-inner">

          {/* TL;DR — passage-citable summary for AI Overview / Perplexity / ChatGPT search */}
          <div className="callout callout-insight">
            <div className="callout-label">TL;DR</div>
            <p>
              An <strong>AI SDR agent</strong> (Artificial Intelligence Sales Development Representative) is software that runs sales development autonomously: qualifying inbound leads, answering product questions, booking demos on a sales rep&rsquo;s calendar, and writing every signal back to the CRM. Humans step in only for complex objections, demo calls, or escalation. This guide covers what AI SDR agents do, how they differ from AI BDRs and AI sales agents, pricing models, deployment, and real customer ROI.
            </p>
          </div>

          {/* WHY CLASSIC FUNNEL BREAKS */}
          <h2>Why the classic inbound funnel breaks</h2>

          <p>
            You invest in SEO, paid ads, content, and LinkedIn. Leads arrive. They visit your pricing page, start a chat, fill out a form. And then nothing happens for hours. Your SDR sees the notification late, sends a generic follow-up, and wonders why the conversion rate is stuck at 2–3%.
          </p>

          {/* Funnel break diagram */}
          <div className="fd-wrap">
            <div className="fd-flow">

              {/* Traffic acquisition */}
              <div className="fd-node">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect width="28" height="28" rx="7" fill="#EBF3FD"/>
                  <path d="M6 20l5-6 4 3 4-7 3 4" stroke="#1A6FD4" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <circle cx="22" cy="8" r="2.5" fill="#1A6FD4"/>
                  <path d="M6 22h16" stroke="#1A6FD4" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                Traffic acquisition
              </div>
              <div className="fd-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 4l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>

              {/* Landing page */}
              <div className="fd-node">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect width="28" height="28" rx="7" fill="#EBF3FD"/>
                  <rect x="7" y="7" width="14" height="14" rx="2" stroke="#1A6FD4" strokeWidth="1.5" fill="none"/>
                  <path d="M10 11h8M10 14h5M10 17h6" stroke="#1A6FD4" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                Landing page
              </div>
              <div className="fd-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 4l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>

              {/* Question */}
              <div className="fd-node">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect width="28" height="28" rx="7" fill="#EBF3FD"/>
                  <path d="M9 10.5a5 5 0 0 1 9.5 1.5c0 2.5-2.5 3-2.5 5" stroke="#1A6FD4" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                  <circle cx="16" cy="19.5" r="1" fill="#1A6FD4"/>
                </svg>
                Question
              </div>
              <div className="fd-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 4l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>

              {/* BROKEN GROUP: Response + Qualification */}
              <div className="fd-broken-group">
                {/* Broken funnel above */}
                <div className="fd-funnel-wrap">
                  <svg width="44" height="38" viewBox="0 0 44 38" fill="none">
                    {/* left half of funnel */}
                    <path d="M4 4h16l-6 12v10l-4-3V16L4 4z" fill="#E8A898" stroke="#D94F33" strokeWidth="1.2" strokeLinejoin="round"/>
                    {/* right half — shifted/broken */}
                    <path d="M40 4H24l6 12v8l4-2V16l6-12z" fill="#E8A898" stroke="#D94F33" strokeWidth="1.2" strokeLinejoin="round"/>
                    {/* crack lines */}
                    <path d="M18 6l4 5M22 4l3 6" stroke="#D94F33" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                </div>

                <div className="fd-node">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect width="28" height="28" rx="7" fill="#FDF0EC"/>
                    <path d="M20 8L8 14l5 1 1 5 6-12z" stroke="#D94F33" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                  </svg>
                  Response
                </div>
                <div className="fd-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 4l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                <div className="fd-node">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect width="28" height="28" rx="7" fill="#FDF0EC"/>
                    <path d="M14 8l-6 6 6 6 6-6-6-6z" stroke="#D94F33" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                    <path d="M11 14l2 2 4-4" stroke="#D94F33" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Qualification
                </div>

                {/* Dots leaking below */}
                <div className="fd-dots-wrap">
                  <div className="fd-dot-row">
                    <div className="fd-dot"/><div className="fd-dot"/><div className="fd-dot"/>
                  </div>
                  <div className="fd-dot-row">
                    <div className="fd-dot sm"/><div className="fd-dot"/><div className="fd-dot sm"/><div className="fd-dot sm"/><div className="fd-dot"/>
                  </div>
                  <div className="fd-dot-row">
                    <div className="fd-dot sm"/><div className="fd-dot sm"/><div className="fd-dot"/><div className="fd-dot sm"/><div className="fd-dot sm"/><div className="fd-dot sm"/>
                  </div>
                </div>
              </div>

              <div className="fd-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 4l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>

              {/* Routing */}
              <div className="fd-node">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect width="28" height="28" rx="7" fill="#EBF3FD"/>
                  <path d="M8 10h5l7 8h0" stroke="#1A6FD4" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                  <path d="M8 18h5l7-8" stroke="#1A6FD4" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                  <circle cx="20" cy="10" r="2" fill="#1A6FD4"/>
                  <circle cx="20" cy="18" r="2" fill="#1A6FD4"/>
                </svg>
                Routing
              </div>
              <div className="fd-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 4l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>

              {/* Meeting booked */}
              <div className="fd-node">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect width="28" height="28" rx="7" fill="#EBF3FD"/>
                  <rect x="7" y="9" width="14" height="12" rx="2" stroke="#1A6FD4" strokeWidth="1.4" fill="none"/>
                  <path d="M7 12h14" stroke="#1A6FD4" strokeWidth="1.4"/>
                  <path d="M11 7v3M17 7v3" stroke="#1A6FD4" strokeWidth="1.4" strokeLinecap="round"/>
                  <path d="M11 16l2 2 4-3" stroke="#1A6FD4" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Meeting booked
              </div>
              <div className="fd-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 4l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>

              {/* CRM */}
              <div className="fd-node">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect width="28" height="28" rx="7" fill="#EBF3FD"/>
                  <ellipse cx="14" cy="10" rx="6" ry="2.5" stroke="#1A6FD4" strokeWidth="1.4" fill="none"/>
                  <path d="M8 10v4c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5v-4" stroke="#1A6FD4" strokeWidth="1.4" fill="none"/>
                  <path d="M8 14v4c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5v-4" stroke="#1A6FD4" strokeWidth="1.4" fill="none"/>
                </svg>
                CRM
              </div>

            </div>
          </div>

          <div className="callout callout-problem">
            <div className="callout-label">The core failure</div>
            <p>
              <strong>70% of B2B buyers choose the vendor who responds first.</strong> The average inbound response time is 42 hours. You are losing deals before a human ever says hello.
            </p>
          </div>

          <p>
            The structural problem is that the inbound funnel was designed around human speed. Humans are not fast enough. Hiring more SDRs is expensive, does not scale to 2am leads from organic search, and still leaves qualification as a manual, inconsistent process.
          </p>

          <div className="stat-strip">
            <div className="stat-item">
              <div className="stat-number">42h</div>
              <div className="stat-label">Average B2B inbound response time</div>
            </div>
            <div className="stat-item">
              <div className="stat-number green">5min</div>
              <div className="stat-label">Response window before lead quality drops 80%</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">70%</div>
              <div className="stat-label">Buyers go with first-to-respond vendor</div>
            </div>
          </div>

          <p>
            The classic inbound funnel has four failure points that compound each other: <strong>slow response</strong> (the lead cools off), <strong>manual qualification</strong> (SDRs ask the same questions on every call), <strong>inconsistent follow-up</strong> (depends on who is on shift), and <strong>poor handoff</strong> (AEs get unqualified leads with no context). AI inbound SDR agents address all four simultaneously. Vendors that ship strong inbound coverage are catalogued in the <a href="https://www.dashly.io/tools/best-ai-sdr-tools/">12 best AI SDR tools pillar</a>.
          </p>

          <hr className="section-divider" />

          {/* WHAT IS AN AI INBOUND SDR AGENT */}
          <h2>What is an AI inbound SDR agent?</h2>

          <p>
            An AI inbound SDR agent is a software system that handles the inbound sales development workflow end-to-end: it engages visitors and leads in real-time conversation, qualifies them against your ICP criteria, answers product and pricing questions, captures structured data, routes qualified leads to the right AE, and runs automated follow-up sequences for leads that are not ready yet.
          </p>

          <p>
            The key distinction from a chatbot or a form is that an AI SDR agent <strong>conducts a conversation</strong>. It adapts its questions based on answers, handles objections, and makes a judgment about lead quality. It reasons about each interaction, not reads from a script.
          </p>

          <div className="callout callout-insight">
            <div className="callout-label">Definition</div>
            <p>
              An AI inbound SDR agent = conversational qualification engine + instant response layer + meeting booking automation + CRM enrichment + nurture sequences. It replaces the first 2–3 touches of your SDR workflow: the repetitive, time-sensitive parts that don't require human judgment.
            </p>
            <p>
              For a broader category overview, see <a href="/resources/what-is-an-ai-revenue-agent/">What are AI revenue agents?</a> — covers the full agent stack (SDR, BDR, AE-assist, CSM-assist) and where AI inbound SDR fits.
            </p>
          </div>

          <p>
            AI agents differ from traditional automation tools (sequences, chatbots, forms) in one critical way: they can <strong>handle the unexpected</strong>. When a lead asks a question that is not in the script, a classic bot fails. An AI agent reasons from your product knowledge base and gives a coherent answer.
          </p>

          <p>
            For the short version with disambiguation from chatbots, AI BDRs, and human SDRs, see <a href="/blog/ai-sdr/">AI SDR meaning: what the term stands for</a> — a 5-minute read with the plain-language definition and a real conversation example.
          </p>

          <hr className="section-divider" />

          {/* AI SDR vs HUMAN SDR vs AI BDR vs AI SALES AGENT */}
          <h2 id="what-is-ai-sdr-vs-bdr">AI SDR vs human SDR vs AI BDR vs AI sales agent</h2>

          <p>
            Four terms get used interchangeably in the market, which creates real confusion when buyers compare vendors. The functional differences:
          </p>

          <table className="compare-table compare-table-wide">
            <thead>
              <tr>
                <th></th>
                <th>Human SDR</th>
                <th>AI SDR</th>
                <th>AI BDR</th>
                <th>AI sales agent</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Primary scope</td>
                <td>Qualification + first outreach</td>
                <td>Qualification + first outreach (inbound + outbound)</td>
                <td>Net-new outbound demand generation</td>
                <td>Anything from prospecting to closing assistance</td>
              </tr>
              <tr>
                <td>Channels</td>
                <td>Phone, email, LinkedIn</td>
                <td>Email, chat, LinkedIn; voice and video on premium plans</td>
                <td>Email and LinkedIn primarily</td>
                <td>All of the above plus CRM updates and pipeline mgmt</td>
              </tr>
              <tr>
                <td>Autonomy</td>
                <td>Full agency, judgment</td>
                <td>Bounded by persona and scripts</td>
                <td>Outbound-focused, less inbound coverage</td>
                <td>Vendor-defined; broadest term</td>
              </tr>
              <tr>
                <td>Typical cost</td>
                <td>$80,000 to $120,000 OTE per rep, US-based</td>
                <td>$24/mo (Dashly inbound) to $900/mo per agent</td>
                <td>$499/mo (Salesforge) to $900/mo (AISDR), often quarterly billed</td>
                <td>$180+ per seat (Outreach, Salesloft)</td>
              </tr>
            </tbody>
          </table>

          <p>
            <strong>AI SDR vs AI BDR.</strong> In practice the two terms overlap. The functional split: <strong>AI BDRs lean outbound</strong> (cold email, LinkedIn prospecting). <strong>AI SDRs span both inbound</strong> (website chat, lead qualification) and outbound. Vendors choose the label that fits their funnel positioning, not a strict industry definition.
          </p>

          <p>
            <strong>AI sales agent</strong> is a broader vendor term covering any AI agent assigned to a sales workflow, including pipeline management, post-meeting follow-ups, or call summarization. It is not a 1:1 replacement for an SDR — closer to a generalized AI sales co-pilot.
          </p>

          <p>
            <strong>AI SDR vs human SDR.</strong> The cost math favors AI for teams shipping over 500 qualified inbound leads or 5,000 outbound prospects per month, where one human cannot cover the volume. Below that, a senior human SDR with full agency still outperforms AI on complex enterprise deals. Most teams in 2026 keep human SDRs on complex deals and let AI handle the qualification + booking layer.
          </p>

          <hr className="section-divider" />

          {/* INBOUND vs OUTBOUND AI SDR */}
          <h2>Inbound AI SDR vs outbound AI SDR</h2>

          <p>
            The inbound/outbound split is the single most important question when picking an AI SDR vendor, because most products specialize. Confusing the two leads to teams paying $900/mo for an outbound agent that should be booking inbound demos, or vice versa. This guide focuses on <strong>inbound AI SDR agents</strong> — the side of the funnel that converts traffic you already paid to acquire.
          </p>

          <table className="compare-table">
            <thead>
              <tr>
                <th></th>
                <th className="col-old">Outbound AI SDR</th>
                <th className="col-new">Inbound AI SDR</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Where it lives</td>
                <td className="col-old">Email inbox, LinkedIn sender</td>
                <td className="col-new">Website chat, in-app messenger, pricing page</td>
              </tr>
              <tr>
                <td>Trigger</td>
                <td className="col-old">A target account list (cold)</td>
                <td className="col-new">A visitor action (pricing page, trial signup, half-filled form)</td>
              </tr>
              <tr>
                <td>Primary task</td>
                <td className="col-old">Open a conversation that did not exist</td>
                <td className="col-new">Convert intent into a booked meeting before the visitor leaves</td>
              </tr>
              <tr>
                <td>Channels</td>
                <td className="col-old">Cold email, LinkedIn DMs, sometimes voice</td>
                <td className="col-new">Web chat, email follow-up, knowledge-base lookup, calendar booking</td>
              </tr>
              <tr>
                <td>Scaling cost</td>
                <td className="col-old">Per-agent ($499–$900/mo) plus deliverability infrastructure</td>
                <td className="col-new">Tiered SaaS, often with free plan; no per-resolution surprises</td>
              </tr>
              <tr>
                <td>Replaces</td>
                <td className="col-old">Cold-call SDRs, prospecting researchers</td>
                <td className="col-new">Forms-first signup flow, slow human qualification queue</td>
              </tr>
              <tr>
                <td>Example tools</td>
                <td className="col-old">AISDR, Salesforge Agent Frank, 11x.ai, Apollo</td>
                <td className="col-new">Dashly, Qualified, Drift (Salesloft)</td>
              </tr>
            </tbody>
          </table>

          <p>
            <strong>Hybrid platforms</strong> like Salesloft and Outreach started as outbound execution layers and added inbound chat through Drift (Salesloft) or partnerships. They cost more per seat but consolidate the stack.
          </p>

          <p>
            The fastest way to pick a side: look at where qualified pipeline currently comes from. <strong>B2B SaaS teams with paid demand-gen and an active product trial loop usually need an inbound AI SDR first</strong> — the website is already converting and an AI agent on top compounds. Teams entering a new geography or vertical with no inbound footprint need an outbound AI SDR to cold-start the funnel.
          </p>

          <hr className="section-divider" />

          {/* WHAT THEY DO */}
          <h2>What AI inbound SDR agents actually do</h2>

          <p>A modern AI inbound SDR agent performs six distinct functions in the sales workflow, mirroring the work an inbound SDR would do across <a href="https://www.dashly.io/ai-inbound-revenue-agents/">qualification, support, and meeting booking</a>. Watch the AI qualify a real lead. The profile fills in as answers arrive:</p>

          {/* Animated qual demo */}
          <div className="qual-demo-wrap">
            <div className="qual-demo-grid">

              {/* Lead Profile */}
              <div className="qdp">
                <div className="qdp-head">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="4" r="2.5" fill="currentColor"/><path d="M1 11c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="currentColor" strokeWidth="1.3" fill="none"/></svg>
                  Lead Profile
                </div>
                <div className="qdp-fields">
                  <div className="qdp-field filled">
                    <span className="qdp-field-label">Name</span>
                    <span className="qdp-field-val">Denis M.</span>
                  </div>
                  <div className="qdp-field">
                    <span className="qdp-field-label">Team size</span>
                    <span className="qdp-field-val" id="qf-team">—</span>
                  </div>
                  <div className="qdp-field">
                    <span className="qdp-field-label">Pain</span>
                    <span className="qdp-field-val" id="qf-pain">—</span>
                  </div>
                  <div className="qdp-field">
                    <span className="qdp-field-label">CRM</span>
                    <span className="qdp-field-val" id="qf-crm">—</span>
                  </div>
                  <div className="qdp-field">
                    <span className="qdp-field-label">Budget</span>
                    <span className="qdp-field-val" id="qf-budget">—</span>
                  </div>
                  <div className="qdp-field">
                    <span className="qdp-field-label">Intent</span>
                    <span className="qdp-field-val" id="qf-intent">—</span>
                  </div>
                </div>
                <div className="qdp-divider" />
                <div className="qdp-mql" id="qdp-mql">
                  <span className="qdp-mql-badge">MQL</span>
                  <span className="qdp-mql-text">Qualified lead — ready for demo</span>
                </div>
                <div className="qdp-crm" id="qdp-crm">
                  <div className="qdp-crm-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="10" rx="2" stroke="white" strokeWidth="1.3"/><path d="M2 6h12" stroke="white" strokeWidth="1.3"/><path d="M6 9.5l1.5 1.5 3-3" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div className="qdp-crm-text">
                    <strong>Sent to CRM</strong>
                    Deal created in Salesforce
                  </div>
                </div>
              </div>

              {/* Chat */}
              <div className="qdc">
                <div className="qdc-header">
                  <svg aria-hidden="true" style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 360 80" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="qdc-pat" x="0" y="0" width="120" height="100" patternUnits="userSpaceOnUse">
                        <path d="M6 4a4 4 0 014-4h14a4 4 0 014 4v9a4 4 0 01-4 4h-6l-4 4v-4H10a4 4 0 01-4-4V4z" stroke="white" strokeWidth="1.3" fill="none" opacity=".13" transform="translate(8,10)"/>
                        <rect x="90" y="60" width="18" height="13" rx="2.5" stroke="white" strokeWidth="1.3" fill="none" opacity=".11"/>
                        <path d="M90 63l9 6 9-6" stroke="white" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity=".11"/>
                        <circle cx="120" cy="15" r="1.5" fill="white" opacity=".14"/>
                        <circle cx="50" cy="95" r="1.5" fill="white" opacity=".11"/>
                      </pattern>
                    </defs>
                    <rect width="360" height="80" fill="url(#qdc-pat)"/>
                  </svg>
                  <div className="qdc-header-back">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M7 1L3 5l4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </div>
                  <div className="qdc-header-info">
                    <div className="qdc-header-name">Dashly AI Agent</div>
                    <div className="qdc-header-status">
                      <span className="qdc-online-dot" />
                      online
                    </div>
                  </div>
                  <div className="qdc-header-menu">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                </div>

                <div className="qdc-messages" id="qdc-messages">
                  <div className="qdc-date-sep">Today</div>
                </div>

                <div className="qdc-typing" id="qdc-typing">
                  <div className="qdc-typing-avatar">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="4" y="8" width="16" height="11" rx="3" fill="white"/><rect x="8" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="13" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="10" y="15" width="4" height="1.5" rx="0.75" fill="#5850EC"/><rect x="10.5" y="5" width="3" height="3" rx="1" fill="white"/><rect x="11.5" y="3" width="1" height="2.5" rx="0.5" fill="white"/><rect x="2" y="11" width="2" height="4" rx="1" fill="white"/><rect x="20" y="11" width="2" height="4" rx="1" fill="white"/></svg>
                  </div>
                  <div className="qdc-typing-bubble">
                    <div className="qdc-typing-dot" /><div className="qdc-typing-dot" /><div className="qdc-typing-dot" />
                  </div>
                </div>

                <div className="qdc-input-bar">
                  <div className="qdc-footer">
                    <span>Powered by ⚡ Dashly</span>
                    <span>Privacy Policy</span>
                  </div>
                  <div className="qdc-input-field">
                    <div className="qdc-input-fake">Type your message...</div>
                    <div className="qdc-input-icons">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/><path d="M8.5 14.5s1 2 3.5 2 3.5-2 3.5-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="10" r="1" fill="currentColor"/><circle cx="15" cy="10" r="1" fill="currentColor"/></svg>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21.44 11.05L12.25 20.24a6 6 0 01-8.49-8.49l9.2-9.19a4 4 0 015.66 5.65l-9.2 9.2a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="9" y="2" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M5 10v2a7 7 0 0014 0v-2M12 19v3M8 22h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-marker">01</div>
              <div className="step-body">
                <h3>Engage — instant, personalized first contact</h3>
                <p>
                  The agent initiates conversation the moment a lead shows intent: opens a chat, visits pricing, or submits a form. Response time is under 10 seconds, day or night. The opening message is personalized based on the page the lead is on, their traffic source, and any CRM data already available.
                </p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-marker">02</div>
              <div className="step-body">
                <h3>Qualify — structured discovery in conversation</h3>
                <p>
                  The agent works through your qualification criteria (BANT, MEDDIC, or a custom framework) through natural dialogue. It asks about company size, use case, budget, timeline, and current tools, adapting the sequence based on answers. Each response is scored and compared to your ICP definition.
                </p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-marker">03</div>
              <div className="step-body">
                <h3>Answer — product knowledge, pricing, objections</h3>
                <p>
                  The agent uses your product knowledge base to answer questions about features, integrations, security, and pricing. When a lead objects ("we tried this before and it did not work"), the agent handles the objection with context from your win/loss patterns. It does not deflect to "talk to our team" for every question.
                </p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-marker">04</div>
              <div className="step-body">
                <h3>Capture — structured data, enriched in CRM</h3>
                <p>
                  As the conversation unfolds, the agent extracts and structures lead data: company name, role, team size, use case, pain points, and evaluation timeline. This data is pushed to your CRM automatically with a conversation summary. AEs arrive at calls fully prepared.
                </p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-marker">05</div>
              <div className="step-body">
                <h3>Route — qualified leads to the right AE instantly</h3>
                <p>
                  Once a lead crosses the MQL threshold, the agent offers to book a demo and routes to the right AE based on territory, segment, or product line. Same conversation, two different outcomes based on qualification score:
                </p>

                {/* Two side-by-side chats: not MQL vs MQL */}
                <div className="route-demo-grid">

                  {/* NOT MQL */}
                  <div className="rdc-wrap">
                    <div className="rdc">
                      <div className="rdc-header">
                        <svg aria-hidden="true" style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 360 60" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <pattern id="rdc1-pat" x="0" y="0" width="120" height="100" patternUnits="userSpaceOnUse">
                              <path d="M6 4a4 4 0 014-4h14a4 4 0 014 4v9a4 4 0 01-4 4h-6l-4 4v-4H10a4 4 0 01-4-4V4z" stroke="white" strokeWidth="1.3" fill="none" opacity=".13" transform="translate(8,10)"/>
                              <rect x="90" y="60" width="18" height="13" rx="2.5" stroke="white" strokeWidth="1.3" fill="none" opacity=".11"/>
                              <circle cx="50" cy="95" r="1.5" fill="white" opacity=".11"/>
                            </pattern>
                          </defs>
                          <rect width="360" height="60" fill="url(#rdc1-pat)"/>
                        </svg>
                        <div className="rdc-header-btn"><svg width="9" height="14" viewBox="0 0 9 14" fill="none"><path d="M7.5 1.5L2 7l5.5 5.5" stroke="white" strokeWidth="1.6" strokeLinecap="round"/></svg></div>
                        <div className="rdc-header-center">
                          <div className="rdc-header-name">Dashly AI Agent</div>
                          <div className="rdc-header-status">
                            <span className="rdc-online-dot" />
                            online
                          </div>
                        </div>
                        <div className="rdc-header-btn"><svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5l5 5 5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round"/></svg></div>
                      </div>
                      <div className="rdc-messages" id="rc1-messages">
                        <div className="rdc-date-sep">Today</div>
                      </div>
                      <div className="rdc-typing" id="rc1-typing">
                        <div className="rdc-typing-avatar">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="4" y="8" width="16" height="11" rx="3" fill="white"/><rect x="8" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="13" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="10" y="15" width="4" height="1.5" rx="0.75" fill="#5850EC"/><rect x="10.5" y="5" width="3" height="3" rx="1" fill="white"/><rect x="11.5" y="3" width="1" height="2.5" rx="0.5" fill="white"/><rect x="2" y="11" width="2" height="4" rx="1" fill="white"/><rect x="20" y="11" width="2" height="4" rx="1" fill="white"/></svg>
                        </div>
                        <div className="rdc-typing-bubble">
                          <div className="rdc-typing-dot"/><div className="rdc-typing-dot"/><div className="rdc-typing-dot"/>
                        </div>
                      </div>
                      <div className="rdc-input-bar">
                        <div className="rdc-footer">
                          <span>Powered by ⚡ Dashly</span>
                          <span>Privacy Policy</span>
                        </div>
                        <div className="rdc-input-field">
                          <div className="rdc-input-fake">Type your message...</div>
                          <div className="rdc-input-icons">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/><path d="M8.5 14.5s1 2 3.5 2 3.5-2 3.5-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="10" r="1" fill="currentColor"/><circle cx="15" cy="10" r="1" fill="currentColor"/></svg>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="9" y="2" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M5 10v2a7 7 0 0014 0v-2M12 19v3M8 22h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="rdc-outcome rdc-outcome-notmql" id="rc1-outcome">
                      <span className="rdc-outcome-badge">Not MQL</span>
                      Added to nurture sequence
                    </div>
                  </div>

                  {/* MQL */}
                  <div className="rdc-wrap">
                    <div className="rdc">
                      <div className="rdc-header">
                        <svg aria-hidden="true" style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 360 60" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <pattern id="rdc2-pat" x="0" y="0" width="120" height="100" patternUnits="userSpaceOnUse">
                              <path d="M6 4a4 4 0 014-4h14a4 4 0 014 4v9a4 4 0 01-4 4h-6l-4 4v-4H10a4 4 0 01-4-4V4z" stroke="white" strokeWidth="1.3" fill="none" opacity=".13" transform="translate(8,10)"/>
                              <rect x="90" y="60" width="18" height="13" rx="2.5" stroke="white" strokeWidth="1.3" fill="none" opacity=".11"/>
                              <circle cx="50" cy="95" r="1.5" fill="white" opacity=".11"/>
                            </pattern>
                          </defs>
                          <rect width="360" height="60" fill="url(#rdc2-pat)"/>
                        </svg>
                        <div className="rdc-header-btn"><svg width="9" height="14" viewBox="0 0 9 14" fill="none"><path d="M7.5 1.5L2 7l5.5 5.5" stroke="white" strokeWidth="1.6" strokeLinecap="round"/></svg></div>
                        <div className="rdc-header-center">
                          <div className="rdc-header-name">Dashly AI Agent</div>
                          <div className="rdc-header-status">
                            <span className="rdc-online-dot" />
                            online
                          </div>
                        </div>
                        <div className="rdc-header-btn"><svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5l5 5 5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round"/></svg></div>
                      </div>
                      <div className="rdc-messages" id="rc2-messages">
                        <div className="rdc-date-sep">Today</div>
                      </div>
                      <div className="rdc-typing" id="rc2-typing">
                        <div className="rdc-typing-avatar">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="4" y="8" width="16" height="11" rx="3" fill="white"/><rect x="8" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="13" y="11.5" width="3" height="3" rx="1" fill="#5850EC"/><rect x="10" y="15" width="4" height="1.5" rx="0.75" fill="#5850EC"/><rect x="10.5" y="5" width="3" height="3" rx="1" fill="white"/><rect x="11.5" y="3" width="1" height="2.5" rx="0.5" fill="white"/><rect x="2" y="11" width="2" height="4" rx="1" fill="white"/><rect x="20" y="11" width="2" height="4" rx="1" fill="white"/></svg>
                        </div>
                        <div className="rdc-typing-bubble">
                          <div className="rdc-typing-dot"/><div className="rdc-typing-dot"/><div className="rdc-typing-dot"/>
                        </div>
                      </div>
                      <div className="rdc-input-bar">
                        <div className="rdc-footer">
                          <span>Powered by ⚡ Dashly</span>
                          <span>Privacy Policy</span>
                        </div>
                        <div className="rdc-input-field">
                          <div className="rdc-input-fake">Type your message...</div>
                          <div className="rdc-input-icons">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/><path d="M8.5 14.5s1 2 3.5 2 3.5-2 3.5-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="10" r="1" fill="currentColor"/><circle cx="15" cy="10" r="1" fill="currentColor"/></svg>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="9" y="2" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M5 10v2a7 7 0 0014 0v-2M12 19v3M8 22h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="rdc-outcome rdc-outcome-mql" id="rc2-outcome">
                      <span className="rdc-outcome-badge">MQL ✓</span>
                      Routed to Chris (AE) · Demo booked
                    </div>
                  </div>

                </div>
              </div>
            </div>
            <div className="process-step">
              <div className="step-marker">06</div>
              <div className="step-body">
                <h3>Nurture — automated follow-up for not-ready leads</h3>
                <p>
                  Leads that are not ready to book get placed into targeted nurture sequences across email, WhatsApp, and SMS, timed to their evaluation cycle. When they show intent again, the agent re-engages and resumes qualification from where it left off.{" "}
                  <a href="/lead-nurturing/">See the multi-channel nurturing engine →</a>
                </p>
              </div>
            </div>
          </div>

          {/* Nurture canvas — outside process-steps, centered on page */}
          <div className="nurture-canvas-wrap">
                  <div className="nc-legend">
                    <span className="nc-legend-item">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="2" width="12" height="9" rx="2" stroke="#5C5B56" strokeWidth="1.2"/><path d="M1 5l6 4 6-4" stroke="#5C5B56" strokeWidth="1.2"/></svg>
                      Email reminders
                    </span>
                    <span className="nc-legend-sep">+</span>
                    <span className="nc-legend-item">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="12" height="12" rx="3" fill="#25D366" opacity="0.18"/><path d="M7 2.5C4.5 2.5 2.5 4.3 2.5 6.5c0 .8.2 1.5.6 2.1L2.5 11l2.5-.6C5.6 10.8 6.3 11 7 11c2.5 0 4.5-1.8 4.5-4S9.5 2.5 7 2.5z" stroke="#25D366" strokeWidth="1.1" fill="none"/></svg>
                      WhatsApp
                    </span>
                    <span className="nc-legend-sep">+</span>
                    <span className="nc-legend-item">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="12" height="12" rx="3" fill="#3A8EF6" opacity="0.18"/><path d="M2 7h10M7 2l5 5-5 5" stroke="#3A8EF6" strokeWidth="1.1" strokeLinecap="round"/></svg>
                      SMS
                    </span>
                    <span className="nc-drag-hint">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                      drag cards
                    </span>
                  </div>

                  <div className="nurture-canvas" id="nurture-canvas">

                    {/* Thread connectors between cards */}
                    {/* E1 → E2 (email row, right edge of E1=208 to left of E2=244) */}
                    <div className="nc-thread" style={{left:'209px', top:'111px', width:'35px'}}/>
                    {/* E2 → E3 (right edge of E2=434 to left of E3=498) */}
                    <div className="nc-thread" style={{left:'435px', top:'111px', width:'63px'}}/>
                    {/* WA → SMS */}
                    <div className="nc-thread" style={{left:'209px', top:'338px', width:'35px'}}/>

                    {/* Lane divider */}
                    <div className="nc-lane-divider" style={{top: '248px'}}/>
                    <div className="nc-lane-tag" style={{top: '14px'}}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><rect x="0.5" y="1" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1"/><path d="M.5 3.5l4.5 3 4.5-3" stroke="currentColor" strokeWidth="1"/></svg>
                      Email
                    </div>
                    <div className="nc-lane-tag" style={{top: '262px'}}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 1C2.8 1 1 2.6 1 4.5c0 .6.2 1.2.5 1.7L1 9l2-.5C3.5 8.8 4.2 9 5 9c2.2 0 4-1.6 4-3.5S7.2 1 5 1z" stroke="currentColor" strokeWidth="1" fill="none"/></svg>
                      WhatsApp · SMS
                    </div>

                    {/* Email card 1 — Booking confirmed */}
                    <div className="nc-card nc-email" style={{left:'18px', top:'42px', transform:'rotate(-0.8deg)'}}>
                      <div className="nc-email-chrome">
                        <div className="nc-email-dots">
                          <div className="nc-email-dot nc-email-dot-r"/><div className="nc-email-dot nc-email-dot-y"/><div className="nc-email-dot nc-email-dot-g"/>
                        </div>
                        <svg width="18" height="10" viewBox="0 0 18 10" fill="none"><path d="M2 5h8M7 2l3 3-3 3" stroke="#bbb" strokeWidth="1.1" strokeLinecap="round"/></svg>
                        <span className="nc-email-timing">Immediately</span>
                      </div>
                      <div className="nc-email-subject">
                        <span className="nc-email-subject-text">Meeting confirmed — see you June 6 at 2:30 PM</span>
                        <span className="nc-email-emoji">✅</span>
                      </div>
                      <div className="nc-email-body">
                        <div className="nc-email-body-text">Hello Denis, your demo with Chris is confirmed. Here's the link to join: <u>meeting link</u>. We're looking forward to speaking with you.</div>
                      </div>
                      <div className="nc-email-footer">
                        <div className="nc-email-avatar"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="6" r="3" fill="#C89070"/><path d="M2 15c0-3.3 2.7-5 6-5s6 1.7 6 5" fill="#5D6B48"/></svg></div>
                        <span className="nc-email-from">Chris</span>
                        <span className="nc-email-date">Jun 5, 1:06 PM</span>
                      </div>
                    </div>

                    {/* Email card 2 — Day before */}
                    <div className="nc-card nc-email" style={{left:'244px', top:'42px', transform:'rotate(0.6deg)'}}>
                      <div className="nc-email-chrome">
                        <div className="nc-email-dots">
                          <div className="nc-email-dot nc-email-dot-r"/><div className="nc-email-dot nc-email-dot-y"/><div className="nc-email-dot nc-email-dot-g"/>
                        </div>
                        <svg width="18" height="10" viewBox="0 0 18 10" fill="none"><path d="M2 5h8M7 2l3 3-3 3" stroke="#bbb" strokeWidth="1.1" strokeLinecap="round"/></svg>
                        <span className="nc-email-timing">Day before</span>
                      </div>
                      <div className="nc-email-subject">
                        <span className="nc-email-subject-text">Your meeting is tomorrow, confirm your participation</span>
                        <span className="nc-email-emoji">🕐</span>
                      </div>
                      <div className="nc-email-body">
                        <div className="nc-email-body-text">Hello Denis, I'd like to remind you that we have a meeting scheduled for June 6 at 2:30 PM. Here's the link to join: <u>meeting link</u>.</div>
                      </div>
                      <div className="nc-email-footer">
                        <div className="nc-email-avatar"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="6" r="3" fill="#C89070"/><path d="M2 15c0-3.3 2.7-5 6-5s6 1.7 6 5" fill="#5D6B48"/></svg></div>
                        <span className="nc-email-from">Chris</span>
                        <span className="nc-email-date">Jun 5, 1:06 PM</span>
                      </div>
                    </div>

                    {/* Email card 3 — 1h before */}
                    <div className="nc-card nc-email" style={{left:'498px', top:'42px', transform:'rotate(-0.5deg)'}}>
                      <div className="nc-email-chrome">
                        <div className="nc-email-dots">
                          <div className="nc-email-dot nc-email-dot-r"/><div className="nc-email-dot nc-email-dot-y"/><div className="nc-email-dot nc-email-dot-g"/>
                        </div>
                        <svg width="18" height="10" viewBox="0 0 18 10" fill="none"><path d="M2 5h8M7 2l3 3-3 3" stroke="#bbb" strokeWidth="1.1" strokeLinecap="round"/></svg>
                        <span className="nc-email-timing">1h before</span>
                      </div>
                      <div className="nc-email-subject">
                        <span className="nc-email-subject-text">Your meeting starts in 1 hour — join here</span>
                        <span className="nc-email-emoji">🔔</span>
                      </div>
                      <div className="nc-email-body">
                        <div className="nc-email-body-text">Hello Denis, this is your final reminder. Your meeting with Chris starts in 1 hour. Join: <u>meeting link</u>. Don't miss this!</div>
                      </div>
                      <div className="nc-email-footer">
                        <div className="nc-email-avatar"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="6" r="3" fill="#C89070"/><path d="M2 15c0-3.3 2.7-5 6-5s6 1.7 6 5" fill="#5D6B48"/></svg></div>
                        <span className="nc-email-from">Chris</span>
                        <span className="nc-email-date">Jun 6, 1:30 PM</span>
                      </div>
                    </div>

                    {/* WhatsApp card */}
                    <div className="nc-card nc-wa" style={{left:'18px', top:'272px', transform:'rotate(0.8deg)'}}>
                      <div className="nc-wa-header">
                        <svg width="8" height="12" viewBox="0 0 8 12" fill="none"><path d="M6 1L2 6l4 5" stroke="#8696A0" strokeWidth="1.4" strokeLinecap="round"/></svg>
                        <div className="nc-wa-avatar">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="9" r="4" fill="#5D7080"/><path d="M4 22c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="#4A5A68"/></svg>
                        </div>
                        <div>
                          <div className="nc-wa-name">+7 999 ···</div>
                          <div className="nc-wa-status">online</div>
                        </div>
                        <div className="nc-wa-icons">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7a5 5 0 0110 0" stroke="currentColor" strokeWidth="1.2" fill="none"/><path d="M5 5l-3 2 3 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="2" cy="7" r="1" fill="currentColor"/><circle cx="7" cy="7" r="1" fill="currentColor"/><circle cx="12" cy="7" r="1" fill="currentColor"/></svg>
                        </div>
                      </div>
                      <div className="nc-wa-msgs">
                        <div className="nc-wa-bubble">Denis, good afternoon! It's Chris from Acme Corp. You have a meeting with our expert scheduled in 4 hours. Let's confirm that everything is still on track. Will you be able to attend?</div>
                        <div className="nc-wa-time2">3:02 pm ✓✓</div>
                      </div>
                      <div className="nc-wa-input">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="#8696A0" strokeWidth="1"/><path d="M5 7.5c.4 1 3.6 1 4 0" stroke="#8696A0" strokeWidth="1" strokeLinecap="round"/><circle cx="4.5" cy="5.5" r=".8" fill="#8696A0"/><circle cx="9.5" cy="5.5" r=".8" fill="#8696A0"/></svg>
                        <div className="nc-wa-input-fake">Message</div>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 2h8l1 6H2L3 2z" stroke="#8696A0" strokeWidth="1" fill="none"/><path d="M5 8v3M9 8v3" stroke="#8696A0" strokeWidth="1" strokeLinecap="round"/></svg>
                        <div className="nc-wa-send">
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 2l3 3-3 3" stroke="white" strokeWidth="1.2" strokeLinecap="round"/></svg>
                        </div>
                      </div>
                    </div>

                    {/* SMS card */}
                    <div className="nc-card nc-sms" style={{left:'244px', top:'272px', transform:'rotate(-1deg)'}}>
                      <div className="nc-sms-header">
                        <span className="nc-sms-back">
                          <svg width="8" height="12" viewBox="0 0 8 12" fill="none"><path d="M6 1L2 6l4 5" stroke="#3A8EF6" strokeWidth="1.4" strokeLinecap="round"/></svg>
                        </span>
                        <div className="nc-sms-avatar">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="5" r="3" fill="#636366"/><path d="M1 13c0-3.3 2.7-5 6-5s6 1.7 6 5" fill="#48484A"/></svg>
                        </div>
                        <span className="nc-sms-name">+7 999 ···</span>
                        <div className="nc-sms-icons">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="2" cy="7" r="1" fill="currentColor"/><circle cx="7" cy="7" r="1" fill="currentColor"/><circle cx="12" cy="7" r="1" fill="currentColor"/></svg>
                        </div>
                      </div>
                      <div className="nc-sms-msgs">
                        <div className="nc-sms-date">Friday, 6 June</div>
                        <div className="nc-sms-bubble">Denis, good afternoon! Your meeting with an Acme Corp expert is in 1 hour. Here's the Google Meet link. We'll be waiting for you!</div>
                        <div className="nc-sms-time">5:14 pm</div>
                      </div>
                      <div className="nc-sms-input">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="12" height="12" rx="3" stroke="#636366" strokeWidth="1"/><path d="M7 4v6M4 7h6" stroke="#636366" strokeWidth="1" strokeLinecap="round"/></svg>
                        <div className="nc-sms-input-fake">iMessage</div>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="#636366" strokeWidth="1"/><path d="M5 7.5c.4 1 3.6 1 4 0" stroke="#636366" strokeWidth="1" strokeLinecap="round"/><circle cx="4.5" cy="5.5" r=".8" fill="#636366"/><circle cx="9.5" cy="5.5" r=".8" fill="#636366"/></svg>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 10V4M4 7l3-3 3 3" stroke="#636366" strokeWidth="1.1" strokeLinecap="round"/></svg>
                      </div>
                    </div>

                    <div className="nc-hint">
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                      drag cards to explore
                    </div>

                  </div>
                </div>

          <hr className="section-divider" />

          {/* HOW THEY WORK — 4-LAYER ARCHITECTURE */}
          <h2>How AI SDRs work under the hood</h2>

          <p>
            An AI SDR is not a single model. It is a four-layer system, and the strength of the weakest layer is the strength of the whole product. Vendors that thin out any one of these layers ship a tool that drifts in production within 60 days.
          </p>

          <div className="arch-grid">
            <div className="arch-card">
              <div className="arch-num">1</div>
              <div className="arch-title">LLM core</div>
              <p className="arch-desc">
                Most AI SDRs in 2026 run on GPT-4-class models (GPT-4o, Claude Sonnet 4.6, Llama 3.1 70B+). The model handles language understanding, intent classification, and response drafting. Vendors that ship fine-tuned variants (11x.ai, Conversica) usually do so on top of an open base model, not from scratch.
              </p>
            </div>
            <div className="arch-card">
              <div className="arch-num">2</div>
              <div className="arch-title">Persona layer</div>
              <p className="arch-desc">
                The configuration that constrains the LLM to a brand voice and ICP. Strong AI SDRs (Dashly, AISDR, 11x.ai) train on 30–50 uploaded writing samples — last-quarter sales emails, support replies, blog posts. Weaker ones rely on a one-line "tone" instruction, which produces generic output that hurts cold-email deliverability and brand perception.
              </p>
            </div>
            <div className="arch-card">
              <div className="arch-num">3</div>
              <div className="arch-title">Knowledge layer (RAG)</div>
              <p className="arch-desc">
                Retrieval-augmented generation, where the agent reads a knowledge base — help center, product docs, sales playbook — at runtime and grounds answers in vendor-verified facts. Without a knowledge layer, AI SDRs hallucinate pricing, integration details, or contract terms, which loses deals after the demo.
              </p>
            </div>
            <div className="arch-card">
              <div className="arch-num">4</div>
              <div className="arch-title">Action layer</div>
              <p className="arch-desc">
                CRM read-write, calendar booking, email send, ticket creation, escalation routing. The action layer is where AI SDR tools differentiate most. Read-only AI agents drift over time as CRM state changes; read-write agents stay in sync, but require deeper integration setup (4 to 6 weeks for Conversica, 11x.ai versus under 1 day for inbound web agents like Dashly).
              </p>
            </div>
          </div>

          <p>
            The four-layer split is the right mental model for evaluation. <strong>An AI SDR without an action layer is a chatbot. An AI SDR without a knowledge layer is a hallucination machine.</strong> Both are common in 2026 vendor launches that move fast and skip integration depth.
          </p>

          <div className="callout callout-insight">
            <div className="callout-label">How Dashly differs</div>
            <p>
              Dashly is one of the few vendors that ship all four layers as a native stack rather than third-party glue. The persona is trained on your brand voice samples, the knowledge layer indexes your help center automatically, the action layer writes back to HubSpot, Salesforce, or Pipedrive natively, and the LLM core is bounded by both. See the four-agent architecture in <a href="https://www.dashly.io/ai-inbound-revenue-agents/">AI Inbound Revenue Agents</a> and the standalone <a href="https://www.dashly.io/ai-qualifier-agent/">AI Qualifier Agent</a>.
            </p>
          </div>

          <hr className="section-divider" />

          {/* COMPARISON TABLE */}
          <h2>AI agent vs. traditional automation: a comparison</h2>

          <p>
            Most teams already have some automation in place: sequences, chatbots, lead scoring. Here is how AI SDR agents differ across the dimensions that matter most for inbound revenue:
          </p>

          <table className="compare-table">
            <thead>
              <tr>
                <th></th>
                <th className="col-old">Traditional automation</th>
                <th className="col-new">AI inbound SDR agent</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Response time</td>
                <td className="col-old">Minutes to hours (email sequences)</td>
                <td className="col-new">Under 10 seconds, 24/7</td>
              </tr>
              <tr>
                <td>Qualification</td>
                <td className="col-old">Static forms, manual SDR calls</td>
                <td className="col-new">Conversational, adaptive, consistent</td>
              </tr>
              <tr>
                <td>Off-script questions</td>
                <td className="col-old">Bot fails or routes to human</td>
                <td className="col-new">Handled from product knowledge base</td>
              </tr>
              <tr>
                <td>CRM data quality</td>
                <td className="col-old">Incomplete, manually entered</td>
                <td className="col-new">Structured, auto-enriched with context</td>
              </tr>
              <tr>
                <td>Meeting booking</td>
                <td className="col-old">SDR sends Calendly link via email</td>
                <td className="col-new">Offered in-conversation, booked instantly</td>
              </tr>
              <tr>
                <td>Scale</td>
                <td className="col-old">Linear — more leads = more SDRs</td>
                <td className="col-new">Horizontal — handles 10x volume at flat cost</td>
              </tr>
              <tr>
                <td>Consistency</td>
                <td className="col-old">Varies by rep, time of day, mood</td>
                <td className="col-new">Identical quality on every interaction</td>
              </tr>
              <tr>
                <td>Objection handling</td>
                <td className="col-old">Depends on rep training</td>
                <td className="col-new">From documented win/loss patterns</td>
              </tr>
            </tbody>
          </table>

          <hr className="section-divider" />

          {/* GUARDRAILS */}
          <h2>Guardrails, objections, and what AI agents do not replace</h2>

          <p>
            Before adopting AI inbound SDR agents, revenue leaders typically raise three concerns. All three are worth addressing directly.
          </p>

          <h3>"Will it say something wrong to a prospect?"</h3>
          <p>
            Modern AI agents operate within a defined knowledge boundary. They answer only from your approved product documentation, pricing guides, and objection-handling playbooks. When asked something outside that boundary, they escalate to a human rather than hallucinate. You control what the agent can and cannot discuss.
          </p>

          <h3>"Won't leads feel like they're talking to a bot?"</h3>
          <p>
            The experience depends entirely on implementation. A poorly configured agent with a generic script feels like a bot. A well-configured agent that knows your product, references the lead's specific situation, and converses naturally is indistinguishable from a thoughtful SDR. Some leads actually prefer it because they're not being sold to in the first message.
          </p>

          <div className="callout callout-warning">
            <div className="callout-label">Implementation note</div>
            <p>
              The quality gap between a well-configured AI agent and a poorly configured one is enormous. The agent is only as good as the knowledge base, qualification criteria, and persona you define. Plan 2&ndash;4 weeks for proper setup and calibration. See <a href="https://www.dashly.io/blog/ai-sdr-best-practices/">AI SDR best practices</a> for setup steps that prevent the most common failures, and <a href="https://www.dashly.io/blog/how-we-messed-up-the-ai-sdr/">how teams mess up AI SDR rollouts</a> for what to avoid.
            </p>
          </div>

          <h3>"What does it not replace?"</h3>
          <p>
            AI inbound SDR agents are not a replacement for AEs, relationship-based enterprise deals, or complex multi-stakeholder negotiations. They handle the top-of-funnel: the repetitive, time-sensitive work that doesn't require a human. Deal strategy, negotiation, and executive relationships remain with your team.
          </p>

          <hr className="section-divider" />

          {/* WILL AI SDRs REPLACE HUMANS */}
          <h2>Will Inbound SDR Agents Replace Human SDRs?</h2>

          {/* Before / After funnel diagram */}
          <div className="funnel-ba-diagram">
            {/*
              viewBox 860×348. Left funnel center x=194, step −52px → narrowest block w=164 fits "Meeting happened 🤝".
              Right side: top bar y=18..61, bottom bar y=256..299. AI card h=136, centered: y=61+(195/2−68)=91.
            */}
            <svg viewBox="0 0 860 348" width="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <filter id="fba-shadow" x="-10%" y="-10%" width="120%" height="130%">
                  <feDropShadow dx="0" dy="3" stdDeviation="8" floodColor="rgba(0,0,0,0.09)"/>
                </filter>
              </defs>

              {/* Background */}
              <rect width="860" height="348" rx="16" fill="#F5F3EE"/>

              {/* ─── LEFT: BEFORE ─── */}
              {/* center x=194, step −52px: 372, 320, 268, 216, 164 */}
              <rect x="8"   y="18" width="372" height="43" rx="7" fill="#2B3A52"/>
              <text x="194" y="40" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12.5" fontWeight="500" fontFamily="system-ui,-apple-system,sans-serif">Marketing channels 🎯</text>

              <rect x="34"  y="70" width="320" height="43" rx="7" fill="#2B3A52"/>
              <text x="194" y="92" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12.5" fontWeight="500" fontFamily="system-ui,-apple-system,sans-serif">Lead generation 👤</text>

              <rect x="60"  y="122" width="268" height="43" rx="7" fill="#2B3A52"/>
              <text x="194" y="144" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12.5" fontWeight="500" fontFamily="system-ui,-apple-system,sans-serif">Lead qualification 🤔</text>

              <rect x="86"  y="174" width="216" height="43" rx="7" fill="#2B3A52"/>
              <text x="194" y="196" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12.5" fontWeight="500" fontFamily="system-ui,-apple-system,sans-serif">Meeting booked 📅</text>

              <rect x="112" y="226" width="164" height="43" rx="7" fill="#2B3A52"/>
              <text x="194" y="248" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12.5" fontWeight="500" fontFamily="system-ui,-apple-system,sans-serif">Meeting happened 🤝</text>

              {/* Scatter: document icons */}
              <g transform="translate(392,63)" opacity="0.36">
                <rect width="20" height="26" rx="3" fill="none" stroke="#6B7280" strokeWidth="1.5"/>
                <path d="M4 8h12M4 13h12M4 18h8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round"/>
              </g>
              <g transform="translate(390,143)" opacity="0.26">
                <rect width="17" height="22" rx="2.5" fill="none" stroke="#6B7280" strokeWidth="1.3"/>
                <path d="M3.5 7h10M3.5 11h10M3.5 15h7" stroke="#6B7280" strokeWidth="1" strokeLinecap="round"/>
              </g>
              <g transform="translate(10,163)" opacity="0.3">
                <rect width="20" height="26" rx="3" fill="none" stroke="#6B7280" strokeWidth="1.5"/>
                <path d="M4 8h12M4 13h12M4 18h8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round"/>
              </g>

              {/* Scatter: dollar signs */}
              <text x="10"  y="256" fill="#6B7280" fontSize="18" opacity="0.36" fontFamily="Georgia,serif">$</text>
              <text x="386" y="226" fill="#6B7280" fontSize="16" opacity="0.30" fontFamily="Georgia,serif">$</text>
              <text x="396" y="50"  fill="#6B7280" fontSize="14" opacity="0.24" fontFamily="Georgia,serif">$</text>

              {/* ─── CENTER ARROW ─── */}
              <text x="428" y="157" fill="#374151" fontSize="13" textAnchor="middle">✦</text>
              <line x1="406" y1="171" x2="444" y2="171" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
              <polygon points="441,166 453,171 441,176" fill="#374151"/>
              <text x="418" y="191" fill="#374151" fontSize="8" textAnchor="middle" opacity="0.36">✦</text>

              {/* ─── RIGHT: AFTER ─── */}
              {/*
                Panel x=460..852. Center x=656.
                Top bar:    y=18..61.   Bottom bar: y=256..299.
                Space btw:  195px.      Card h=136 → top y=91, bottom y=227, center y=159.
                Gears at y=159 (card vertical center).
              */}

              {/* Top bar */}
              <rect x="460" y="18" width="392" height="43" rx="7" fill="#2B3A52"/>
              <text x="656" y="40" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12.5" fontWeight="500" fontFamily="system-ui,-apple-system,sans-serif">Marketing channels 🎯</text>

              {/* Dashed connector: top bar → AI card */}
              <line x1="656" y1="61" x2="656" y2="91" stroke="#B0BAC7" strokeWidth="1.5" strokeDasharray="3,2"/>

              {/* Left gear — centered at card vertical center y=159 */}
              <g transform="translate(461,159)" fill="#C5CDD9" fillRule="evenodd">
                <path d="M19.18-5.68L25.86-2.72L25.86 2.72L19.18 5.68A20 20 0 0 1 17.58 9.54L20.21 16.36L16.36 20.21L9.54 17.58A20 20 0 0 1 5.68 19.18L2.72 25.86L-2.72 25.86L-5.68 19.18A20 20 0 0 1-9.54 17.58L-16.36 20.21L-20.21 16.36L-17.58 9.54A20 20 0 0 1-19.18 5.68L-25.86 2.72L-25.86-2.72L-19.18-5.68A20 20 0 0 1-17.58-9.54L-20.21-16.36L-16.36-20.21L-9.54-17.58A20 20 0 0 1-5.68-19.18L-2.72-25.86L2.72-25.86L5.68-19.18A20 20 0 0 1 9.54-17.58L16.36-20.21L20.21-16.36L17.58-9.54A20 20 0 0 1 19.18-5.68ZM8 0A8 8 0 1 0 8 0.001Z"/>
              </g>

              {/* Right gear — centered at card vertical center y=159 */}
              <g transform="translate(851,159)" fill="#C5CDD9" fillRule="evenodd">
                <path d="M19.18-5.68L25.86-2.72L25.86 2.72L19.18 5.68A20 20 0 0 1 17.58 9.54L20.21 16.36L16.36 20.21L9.54 17.58A20 20 0 0 1 5.68 19.18L2.72 25.86L-2.72 25.86L-5.68 19.18A20 20 0 0 1-9.54 17.58L-16.36 20.21L-20.21 16.36L-17.58 9.54A20 20 0 0 1-19.18 5.68L-25.86 2.72L-25.86-2.72L-19.18-5.68A20 20 0 0 1-17.58-9.54L-20.21-16.36L-16.36-20.21L-9.54-17.58A20 20 0 0 1-5.68-19.18L-2.72-25.86L2.72-25.86L5.68-19.18A20 20 0 0 1 9.54-17.58L16.36-20.21L20.21-16.36L17.58-9.54A20 20 0 0 1 19.18-5.68ZM8 0A8 8 0 1 0 8 0.001Z"/>
              </g>

              {/* AI SDR Agent card — x=521 w=270, center x=656, y=91 h=136, center y=159 */}
              <rect x="521" y="91" width="270" height="136" rx="12" fill="white" stroke="#DDD9D0" strokeWidth="1.5" filter="url(#fba-shadow)"/>

              {/* Robot avatar — centered at (552, 159) */}
              <g transform="translate(552,159)">
                {/* Antenna */}
                <line x1="0" y1="-38" x2="0" y2="-28" stroke="#9AABBE" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx="0" cy="-41" r="4.5" fill="#D0FF37"/>
                {/* Head */}
                <rect x="-18" y="-27" width="36" height="30" rx="6" fill="#9AABBE"/>
                {/* Eye left */}
                <rect x="-13" y="-20" width="9" height="7" rx="1.5" fill="#D0FF37"/>
                {/* Eye right */}
                <rect x="4" y="-20" width="9" height="7" rx="1.5" fill="#D0FF37"/>
                {/* Mouth */}
                <rect x="-9" y="-8" width="18" height="3.5" rx="1.5" fill="#2B3A52" opacity="0.55"/>
                {/* Neck */}
                <rect x="-4" y="3" width="8" height="5" rx="1.5" fill="#8499B0"/>
                {/* Body */}
                <rect x="-15" y="8" width="30" height="20" rx="4" fill="#9AABBE"/>
                {/* Body center button */}
                <circle cx="0" cy="17" r="3" fill="#D0FF37"/>
              </g>

              {/* Card text — vertically centered in card (top 91, center 159, bottom 227) */}
              <text x="592" y="143" fill="#2B3A52" fontSize="13" fontWeight="700" fontFamily="system-ui,-apple-system,sans-serif">AI SDR agent for</text>
              <text x="592" y="161" fill="#2B3A52" fontSize="13" fontWeight="700" fontFamily="system-ui,-apple-system,sans-serif">your inbound</text>
              <text x="592" y="179" fill="#2B3A52" fontSize="13" fontWeight="700" fontFamily="system-ui,-apple-system,sans-serif">funnel</text>

              {/* Dashed connector: AI card → bottom bar */}
              <line x1="656" y1="227" x2="656" y2="256" stroke="#B0BAC7" strokeWidth="1.5" strokeDasharray="3,2"/>

              {/* Corner connection nodes */}
              <circle cx="460" cy="97"  r="4" fill="none" stroke="#B0BAC7" strokeWidth="1.5"/>
              <circle cx="852" cy="97"  r="4" fill="none" stroke="#B0BAC7" strokeWidth="1.5"/>
              <circle cx="460" cy="221" r="4" fill="none" stroke="#B0BAC7" strokeWidth="1.5"/>
              <circle cx="852" cy="221" r="4" fill="none" stroke="#B0BAC7" strokeWidth="1.5"/>

              {/* Left chain */}
              <line x1="424" y1="221" x2="456" y2="221" stroke="#B0BAC7" strokeWidth="1" strokeDasharray="3,2"/>
              <circle cx="420" cy="221" r="3" fill="none" stroke="#B0BAC7" strokeWidth="1"/>

              {/* Right chain */}
              <line x1="856" y1="221" x2="874" y2="221" stroke="#B0BAC7" strokeWidth="1" strokeDasharray="3,2"/>
              <circle cx="878" cy="221" r="3" fill="none" stroke="#B0BAC7" strokeWidth="1"/>
              <line x1="881" y1="221" x2="890" y2="221" stroke="#B0BAC7" strokeWidth="1"/>

              {/* Top-right chain decoration */}
              <line x1="855" y1="40" x2="869" y2="40" stroke="#B0BAC7" strokeWidth="1" strokeDasharray="2,2"/>
              <circle cx="873" cy="40" r="3" fill="none" stroke="#B0BAC7" strokeWidth="1"/>
              <line x1="876" y1="40" x2="886" y2="40" stroke="#B0BAC7" strokeWidth="1" strokeDasharray="2,2"/>

              {/* Lightning bolts */}
              <text x="448" y="252" fill="#A0AEC0" fontSize="22" fontFamily="system-ui">⚡</text>
              <text x="856" y="166" fill="#A0AEC0" fontSize="18" fontFamily="system-ui">⚡</text>

              {/* Bottom bar */}
              <rect x="534" y="256" width="244" height="43" rx="7" fill="#2B3A52"/>
              <text x="656" y="278" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12.5" fontWeight="500" fontFamily="system-ui,-apple-system,sans-serif">Meeting happened 🤝</text>

              {/* Captions */}
              <text x="194" y="330" textAnchor="middle" fill="#6B7280" fontSize="12.5" fontFamily="system-ui,-apple-system,sans-serif">Before: Manual, long, expensive</text>
              <text x="656" y="330" textAnchor="middle" fill="#6B7280" fontSize="12.5" fontFamily="system-ui,-apple-system,sans-serif">After: Fast, automated, scalable</text>
            </svg>
          </div>

          <p>
            For inbound qualification: yes, partially. For outbound, strategic deal progression, and complex multi-threading: no.
          </p>

          <p>
            The nuanced answer is that the SDR role is bifurcating. The repetitive top-of-funnel work (first response, initial qualification, meeting booking, follow-up) is being automated. What remains is the work that requires human judgment: researching enterprise accounts, navigating buying committees, running multi-thread outbound campaigns, and managing relationships.
          </p>

          <div className="pullquote">
            <p>AI agents handle the volume. Humans handle the complexity. The teams that thrive are those who use AI to clear the repetitive work so SDRs can focus on the deals that actually require judgment.</p>
            <p className="qs">Observed pattern across 50+ Dashly deployments</p>
          </div>

          <p>
            In practice, companies that deploy AI inbound SDR agents typically redeploy their SDR capacity upmarket: larger deals, outbound campaigns, and strategic accounts. Headcount rarely decreases. The output per SDR increases because they are spending their time on higher-value work.
          </p>

          <hr className="section-divider" />

          {/* HOW THEY HELP GROW PIPELINE */}
          <h2>How AI inbound SDR agents help grow pipeline: 4 pillars</h2>

          <h3>1. Speed-to-lead eliminates the biggest single conversion lever</h3>
          <p>
            Responding in under 5 minutes instead of 42 hours is not an incremental improvement. It&rsquo;s a different category of performance. Studies consistently show that lead quality drops 80% after the first 5 minutes. An AI agent running 24/7 captures that window on every lead, regardless of when they arrive. For a teardown of how this changes the funnel economics versus running a human SDR team, see <a href="https://www.dashly.io/blog/ai-sdr-vs-human-sdr/">AI SDR vs human SDR</a>.
          </p>

          <h3>2. Consistent qualification improves pipeline quality</h3>
          <p>
            Human qualification quality varies by rep, time of day, and workload. AI agents apply the same framework on every interaction. The result: fewer unqualified leads in the pipeline, more accurate MQL scoring, and AEs spending time on deals that are genuinely qualified.
          </p>

          <h3>3. In-conversation booking eliminates scheduling friction</h3>
          <p>
            The traditional flow (qualify on a call, send a calendar link by email, wait for the lead to book) introduces 24–72 hours of delay and significant drop-off. When meeting booking happens inside the qualifying conversation at the moment of peak interest, booking rates increase dramatically.
          </p>

          <h3>4. Automated nurture keeps not-ready leads warm</h3>
          <p>
            Most inbound leads are not ready to buy today. Without a nurture system, they get dropped or put into generic email sequences. AI agents maintain personalized follow-up based on the qualification conversation, re-engaging leads when they show intent again.
          </p>

          <div className="stat-strip">
            <div className="stat-item">
              <div className="stat-number green">60-80%</div>
              <div className="stat-label">Reduction in SDR time on top-of-funnel tasks (SaaS industry benchmark)</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">2-3x</div>
              <div className="stat-label">Increase in meetings booked per month after AI SDR deployment</div>
            </div>
            <div className="stat-item">
              <div className="stat-number green">30-50%</div>
              <div className="stat-label">MQL quality improvement from consistent AI qualification</div>
            </div>
          </div>

          <hr className="section-divider" />

          {/* AI SDR PRICING MODELS 2026 */}
          <h2 id="ai-sdr-pricing-models">AI SDR pricing models in 2026</h2>

          <p>
            Pricing structure matters more than headline price. The same $500/mo budget buys very different volume depending on the model. Five pricing models dominate the AI SDR market in 2026:
          </p>

          <table className="compare-table compare-table-wide">
            <thead>
              <tr>
                <th>Model</th>
                <th>Examples</th>
                <th>How it scales</th>
                <th>Watch out for</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Per-resolution</strong></td>
                <td>Intercom Fin (about $0.99 / resolved conversation)</td>
                <td>Pay only for AI conversations that close</td>
                <td>Bills spike unpredictably at peak volume; hard to forecast</td>
              </tr>
              <tr>
                <td><strong>Per-agent</strong></td>
                <td>AISDR ($900/mo), Salesforge Agent Frank ($499/mo)</td>
                <td>Flat monthly per AI agent seat</td>
                <td>High floor; often billed quarterly, no monthly option</td>
              </tr>
              <tr>
                <td><strong>Per-seat (human)</strong></td>
                <td>Outreach, Salesloft, Regie.ai ($180+/seat)</td>
                <td>Tied to human SDR headcount</td>
                <td>Doesn&rsquo;t capture AI-only usage; legacy pricing model</td>
              </tr>
              <tr>
                <td><strong>Tiered SaaS</strong></td>
                <td>Dashly ($24/mo entry, free plan available)</td>
                <td>All-inclusive flat tiers</td>
                <td>Per-tier feature gates; check what&rsquo;s behind the next tier</td>
              </tr>
              <tr>
                <td><strong>Custom enterprise</strong></td>
                <td>Conversica, 11x.ai</td>
                <td>Negotiated annual contracts</td>
                <td>Opaque; expect $30k+ ARR floor and 4&ndash;6 week procurement</td>
              </tr>
            </tbody>
          </table>

          <p>
            <strong>Free tiers worth piloting in 2026.</strong> Dashly ships a free plan with one AI agent and basic qualification. Apollo includes 250 free outbound emails per month on the entry plan. Artisan offers 300 free outbound credits to test the Ava agent. Free tiers let teams ship a working AI SDR in production in under a week with zero spend; useful as proof before a budget conversation.
          </p>

          <p>
            <strong>True cost framing.</strong> The total cost of an AI SDR in year one is monthly subscription plus integration setup (CRM mapping, knowledge base ingestion, persona training) plus onboarding labor. For inbound web-based agents like Dashly, total year-one cost typically runs <strong>1.2 to 1.5x the listed subscription</strong>. For autonomous outbound agents (Conversica, 11x.ai) with deep CRM and persona setup, year-one total runs <strong>2 to 3x the listed price</strong>. Compare apples-to-apples in the <a href="https://www.dashly.io/tools/best-ai-sdr-tools/">12 best AI SDR tools pillar</a> before signing.
          </p>

          <hr className="section-divider" />

          {/* CHOOSING A PLATFORM */}
          <h2>How to choose the right AI inbound SDR platform</h2>

          <p>When evaluating platforms, eight criteria separate production-ready solutions from prototypes. For a side-by-side comparison of 12 vendors against these criteria, see the <a href="https://www.dashly.io/tools/best-ai-sdr-tools/">12 best AI SDR tools pillar</a>.</p>

          <ul>
            <li><strong>Qualification depth.</strong> Can you configure the exact BANT/MEDDIC/custom criteria you use? Does the agent reason about answers or just check boxes?</li>
            <li><strong>Knowledge base control.</strong> Can you upload your product docs, pricing tiers, objection guides, and keep them current? What happens when the agent does not know the answer?</li>
            <li><strong>CRM integration.</strong> Does it write structured data to your CRM in real time, or just dump a transcript? Does it create contacts, update fields, and log activities automatically?</li>
            <li><strong>Meeting booking.</strong> Does it book directly inside the conversation? Does it connect to your AEs' calendars and respect availability, routing rules, and time zones?</li>
            <li><strong>Full-funnel analytics.</strong> Can you trace every lead from first message to closed deal, not just conversation volume? You need <a href="#metrics-roi">conversion rates at each stage</a>: qualification rate, MQL-to-meeting rate, pipeline contribution, and ROI. Platforms that show only chat counts are measuring activity, not revenue impact.</li>
            <li><strong>Agent eval analytics.</strong> This is the capability most platforms skip. It determines whether your AI agent improves over time or quietly degrades. Production-grade platforms provide a dedicated evaluation layer that monitors the agent's own behavior quality across multiple dimensions:
              <ul className="eval-list">
                <li><strong>Hallucination rate</strong> — how often the agent states facts not present in your knowledge base (invented pricing, non-existent features, wrong policies). Measured by cross-referencing responses against your approved knowledge sources.</li>
                <li><strong>Technical failure rate</strong> — sessions where the agent got stuck in a loop, failed to respond, produced a broken message, or timed out. Even a 2–3% failure rate at scale means hundreds of lost leads per month.</li>
                <li><strong>Handoff accuracy</strong> — did the agent correctly identify conversations that required human escalation, and did it transfer with enough context for the SDR to continue without asking the lead to repeat themselves?</li>
                <li><strong>Tone of voice compliance</strong> — does the agent stay within your brand's communication style: formality level, emoji policy, sentence length, vocabulary restrictions? Drift here erodes trust faster than any qualification failure.</li>
                <li><strong>Goal completion rate</strong> — how often does a session reach a defined endpoint (qualified, disqualified, meeting booked, or intentional handoff) vs. ending without resolution?</li>
                <li><strong>Out-of-scope deflection quality</strong> — when leads ask questions outside the agent's defined scope (competitor comparisons, legal questions, requests to speak to a founder), does the agent deflect gracefully or go off-script?</li>
              </ul>
              Without eval analytics, you are flying blind. You will not know if a knowledge base update introduced hallucinations, if a tone change broke brand consistency, or if handoff quality dropped after a prompt edit.
            </li>
            <li><strong>Setup time and ongoing calibration.</strong> How long to go live? Who maintains the agent? What does the improvement loop look like?</li>
          </ul>

          <div className="callout callout-result">
            <div className="callout-label">Why Dashly</div>
            <p>
              Dashly's AI Qualifier Agent is purpose-built for inbound B2B revenue teams. It handles end-to-end qualification, meeting booking, CRM enrichment, and nurture sequences. Full-funnel analytics from conversation to closed deal are included. Setup takes days, not months. The agent is configurable without engineering involvement.
            </p>
          </div>

          <hr className="section-divider" />

          {/* TOP 3 AI SDR TOOLS — TEASER → PILLAR */}
          <h2>Top 3 AI SDR tools for 2026</h2>

          <p>
            The pillar <a href="https://www.dashly.io/tools/best-ai-sdr-tools/">12 best AI SDR tools in 2026</a> reviews all twelve with verified G2 ratings, real review highlights, and side-by-side pricing. Three stand out by category:
          </p>

          <div className="tools-stack">
            <div className="tools-card">
              <div className="tools-card-head">
                <div className="tools-card-head-text">
                  <div className="tools-card-name">Dashly</div>
                  <div className="tools-card-sub">Inbound AI SDR &middot; four agents on a native CDP</div>
                </div>
                <div className="tools-card-pill">Best for inbound</div>
              </div>
              <div className="tools-card-body">
                <div className="tools-card-metric">
                  <div className="tools-card-v">$24/mo</div>
                  <div className="tools-card-l">Starting price &middot; free plan available</div>
                </div>
                <div className="tools-card-metric">
                  <div className="tools-card-v">568%</div>
                  <div className="tools-card-l">ROMI &middot; <a href="https://www.dashly.io/blog/ai-sdr-in-martech-case-study/">InfluADS case</a></div>
                </div>
                <div className="tools-card-metric">
                  <div className="tools-card-v">&lt; 1 day</div>
                  <div className="tools-card-l">Setup time</div>
                </div>
              </div>
              <a className="tools-card-link" href="https://www.dashly.io/tools/ai-sdr-tools/dashly/">View Dashly profile &rarr;</a>
            </div>

            <div className="tools-card">
              <div className="tools-card-head">
                <div className="tools-card-head-text">
                  <div className="tools-card-name">AISDR</div>
                  <div className="tools-card-sub">Autonomous outbound &middot; email + LinkedIn</div>
                </div>
                <div className="tools-card-pill">Best for outbound</div>
              </div>
              <div className="tools-card-body">
                <div className="tools-card-metric">
                  <div className="tools-card-v">$900/mo</div>
                  <div className="tools-card-l">Per agent &middot; quarterly billed</div>
                </div>
                <div className="tools-card-metric">
                  <div className="tools-card-v">2</div>
                  <div className="tools-card-l">Channels &middot; email, LinkedIn</div>
                </div>
                <div className="tools-card-metric">
                  <div className="tools-card-v">SMB</div>
                  <div className="tools-card-l">Target segment</div>
                </div>
              </div>
              <a className="tools-card-link" href="https://www.dashly.io/tools/ai-sdr-tools/aisdr/">View AISDR profile &rarr;</a>
            </div>

            <div className="tools-card">
              <div className="tools-card-head">
                <div className="tools-card-head-text">
                  <div className="tools-card-name">11x.ai</div>
                  <div className="tools-card-sub">Full multi-channel &middot; email, LinkedIn, voice, video</div>
                </div>
                <div className="tools-card-pill">Best for enterprise</div>
              </div>
              <div className="tools-card-body">
                <div className="tools-card-metric">
                  <div className="tools-card-v">$30k+</div>
                  <div className="tools-card-l">ARR floor</div>
                </div>
                <div className="tools-card-metric">
                  <div className="tools-card-v">4&ndash;6 wk</div>
                  <div className="tools-card-l">Procurement</div>
                </div>
                <div className="tools-card-metric">
                  <div className="tools-card-v">4</div>
                  <div className="tools-card-l">Channels including voice</div>
                </div>
              </div>
              <a className="tools-card-link" href="https://www.dashly.io/tools/ai-sdr-tools/11x/">View 11x.ai profile &rarr;</a>
            </div>
          </div>

          <p>
            Looking at all twelve tools side-by-side helps clarify which side of your funnel needs the most help. The pillar includes 9 more tools (Conversica, Qualified, Drift, Salesforge, Apollo, Artisan, Regie.ai, Outreach, Salesloft) with the same depth of pros, cons, and verified G2 reviews.
          </p>

          <p>
            <a href="https://www.dashly.io/tools/best-ai-sdr-tools/"><strong>See all 12 AI SDR tools compared &rarr;</strong></a>
          </p>

          <hr className="section-divider" />

          {/* RESULTS BY SEGMENT */}
          <h2>Results by company segment</h2>

          <h3>SaaS (50–500 employees)</h3>
          <p>
            The highest-impact use case. High inbound volume, well-defined ICP, and a relatively short sales cycle means AI qualification delivers fast, measurable results. Typical outcomes: 60–80% reduction in SDR time on top-of-funnel, 2–3x increase in meetings booked per month, MQL quality improvement of 30–50%.
          </p>

          <h3>Mid-market B2B services</h3>
          <p>
            Longer sales cycles and more complex qualification criteria require a more sophisticated configuration. The agent handles initial discovery and ICP fit; humans take over for multi-stakeholder deals. Typical outcomes: 40–60% of qualified pipeline originated by AI agent within 90 days of deployment.
          </p>

          <h3>Enterprise-motion companies</h3>
          <p>
            AI agents work best at the top of the funnel: initial engagement, routing to the right enterprise AE, and enriching CRM with conversation context before the first human call. They do not replace the relationship-building required for 6–12 month enterprise cycles, but they dramatically improve the quality of the first human touchpoint.
          </p>

          {/* NAMED CUSTOMER OUTCOMES — passage-citable for AI Overview */}
          <h3>Named customer outcomes from production deployments</h3>
          <p>
            Three real outcomes from teams running Dashly&rsquo;s inbound AI SDR in production. All numbers are first-party, published in case studies, not industry estimates.
          </p>

          <div className="outcomes-strip">
            <div className="outcomes-stat">
              <div className="outcomes-v">568%</div>
              <div className="outcomes-l">ROMI &middot; <strong>InfluADS</strong></div>
              <div className="outcomes-d">MarTech platform deployed Dashly&rsquo;s four-agent inbound stack. 70% of qualified leads now book a demo without any human SDR. <a href="https://www.dashly.io/blog/ai-sdr-in-martech-case-study/">Case study &rarr;</a></div>
            </div>
            <div className="outcomes-stat">
              <div className="outcomes-v">+536%</div>
              <div className="outcomes-l">Booked meetings &middot; <strong>Advertising Socials</strong></div>
              <div className="outcomes-d">SaaS / MarTech vendor replaced forms-first signup with Dashly&rsquo;s inbound AI SDR. +536% qualified meetings in 3 months, no headcount increase. <a href="https://www.dashly.io/blog/case-study-saas-martech/">Case study &rarr;</a></div>
            </div>
            <div className="outcomes-stat">
              <div className="outcomes-v">&minus;60%</div>
              <div className="outcomes-l">SDR cost &middot; <strong>VoiceFirst</strong></div>
              <div className="outcomes-d">Custom communications provider deployed Dashly&rsquo;s inbound AI SDR stack. 60% reduction in cost per qualified lead, no pipeline volume loss. <a href="https://www.dashly.io/blog/case-study-ai-cut-costs/">Case study &rarr;</a></div>
            </div>
          </div>

          <p>
            The pattern across all three: the AI SDR did not replace closing reps; it replaced the slow, manual, high-error qualification + booking layer between marketing and sales. Pipeline ROI compounded because deals reached AEs faster and at a higher qualification bar. For a counterexample (where rollouts fail), see <a href="https://www.dashly.io/blog/how-we-messed-up-the-ai-sdr/">How we messed up the AI SDR</a>.
          </p>

          {/* ANALYTICS BLOCK — Dashly own case */}
          <div className="analytics-block">
            <div className="analytics-inner">

              <div className="analytics-head">
                <span className="analytics-tag">Dashly case · Analytics</span>
                <h3 className="analytics-title">
                  End-to-end analytics shows{" "}
                  <mark className="analytics-mark">Agent vs Form</mark>{" "}
                  effectiveness on-site
                </h3>
                <p className="analytics-desc">
                  Real data from Dashly's own inbound pipeline: one quarter, 470 contacts across all qualification scenarios. The AI agent scenario accounts for 72.5% of total pipeline while handling 81% of all contacts.
                </p>
              </div>

              <div className="analytics-kpis">
                <div className="analytics-kpi analytics-kpi-accent">
                  <div className="analytics-kpi-num">72.5%</div>
                  <div className="analytics-kpi-label">
                    <strong>of total pipeline — from AI agent</strong>
                    <span>$5,623,104 out of $7,755,546</span>
                  </div>
                </div>
                <div className="analytics-kpi">
                  <div className="analytics-kpi-num">72%</div>
                  <div className="analytics-kpi-label">
                    <strong>meeting conversion for AI-qualified leads</strong>
                    <span>best among all scenarios</span>
                  </div>
                </div>
                <div className="analytics-kpi">
                  <div className="analytics-kpi-num">81%</div>
                  <div className="analytics-kpi-label">
                    <strong>of all contacts handled by AI agent</strong>
                    <span>381 out of 470 contacts</span>
                  </div>
                </div>
              </div>

              <div className="analytics-table-wrap">
                <div className="analytics-table-title">Conversions by scenario</div>
                <div className="analytics-table-sub">(from contact)</div>
                <div className="analytics-table-scroll">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Scenario</th>
                        <th>Contact</th>
                        <th>Qual %</th>
                        <th>Qualified</th>
                        <th>MQL / Qual %</th>
                        <th>MQL</th>
                        <th>Mtg booked / MQL %</th>
                        <th>Mtg booked</th>
                        <th>Mtg held %</th>
                        <th>Mtg held</th>
                        <th>Paid / Mtg %</th>
                        <th>Paid</th>
                        <th>Lost</th>
                        <th>Pipeline</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="at-row-ai">
                        <td className="at-scenario">
                          AI agent
                          <span className="at-ai-badge">
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="6" fill="#0D9E75"/><path d="M3.5 6h5M6 3.5v5" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/></svg>
                            AI
                          </span>
                        </td>
                        <td>381</td>
                        <td><span className="at-pill at-pill-teal">70%</span></td>
                        <td>268</td>
                        <td><span className="at-pill at-pill-teal">43%</span></td>
                        <td>116</td>
                        <td><span className="at-pill at-pill-teal">55%</span></td>
                        <td>64</td>
                        <td><span className="at-pill at-pill-teal">72%</span></td>
                        <td>46</td>
                        <td><span className="at-pill at-pill-teal">33%</span></td>
                        <td>15</td>
                        <td>20</td>
                        <td className="at-pipeline"><strong>$5,623,104</strong></td>
                      </tr>
                      <tr>
                        <td className="at-scenario">form + quiz</td>
                        <td>66</td>
                        <td><span className="at-pill at-pill-teal">100%</span></td>
                        <td>66</td>
                        <td><span className="at-pill at-pill-teal">79%</span></td>
                        <td>52</td>
                        <td><span className="at-pill at-pill-teal">67%</span></td>
                        <td>35</td>
                        <td><span className="at-pill at-pill-teal">89%</span></td>
                        <td>31</td>
                        <td><span className="at-pill at-pill-light">16%</span></td>
                        <td>5</td>
                        <td>8</td>
                        <td className="at-pipeline">$1,516,488</td>
                      </tr>
                      <tr>
                        <td className="at-scenario">form only</td>
                        <td>20</td>
                        <td><span className="at-pill at-pill-teal">65%</span></td>
                        <td>13</td>
                        <td><span className="at-pill at-pill-teal">69%</span></td>
                        <td>9</td>
                        <td><span className="at-pill at-pill-light">22%</span></td>
                        <td>2</td>
                        <td><span className="at-pill at-pill-teal">100%</span></td>
                        <td>2</td>
                        <td><span className="at-pill at-pill-light">0%</span></td>
                        <td>0</td>
                        <td>3</td>
                        <td className="at-pipeline">$30,090</td>
                      </tr>
                      <tr>
                        <td className="at-scenario">form + AI</td>
                        <td>3</td>
                        <td><span className="at-pill at-pill-teal">100%</span></td>
                        <td>3</td>
                        <td><span className="at-pill at-pill-teal">67%</span></td>
                        <td>2</td>
                        <td><span className="at-pill at-pill-teal">100%</span></td>
                        <td>2</td>
                        <td><span className="at-pill at-pill-teal">100%</span></td>
                        <td>2</td>
                        <td><span className="at-pill at-pill-teal">50%</span></td>
                        <td>1</td>
                        <td>0</td>
                        <td className="at-pipeline">$585,864</td>
                      </tr>
                      <tr className="at-row-total">
                        <td className="at-scenario"><strong>Grand total</strong></td>
                        <td><strong>470</strong></td>
                        <td><span className="at-pill at-pill-teal">74%</span></td>
                        <td><strong>350</strong></td>
                        <td><span className="at-pill at-pill-teal">51%</span></td>
                        <td><strong>179</strong></td>
                        <td><span className="at-pill at-pill-teal">58%</span></td>
                        <td><strong>103</strong></td>
                        <td><span className="at-pill at-pill-teal">79%</span></td>
                        <td><strong>81</strong></td>
                        <td><span className="at-pill at-pill-teal">26%</span></td>
                        <td><strong>21</strong></td>
                        <td><strong>31</strong></td>
                        <td className="at-pipeline"><strong>$7,755,546</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>

          <hr className="section-divider" />

          {/* METRICS & ROI */}
          <h2 id="metrics-roi">Metrics and ROI: what to measure</h2>

          <p>Track these metrics to evaluate your AI inbound SDR agent's performance:</p>

          <div className="metrics-groups">

            {/* Group 1 — Funnel conversion */}
            <div className="metrics-group">
              <div className="metrics-group-label mg-blue">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2h10v2L8 8v4l-2-1V8L2 4V2z" stroke="#1A6FD4" strokeWidth="1.3" strokeLinejoin="round" fill="none"/></svg>
                Funnel conversion
              </div>
              <div className="metrics-row">
                <div className="metric-card mc-blue">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 3h14v2l-4 4v5l-2.5 1.5-1.5-1V9L2 5V3z" stroke="#1A6FD4" strokeWidth="1.3" strokeLinejoin="round" fill="none"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">Conversation-to-qualification rate</div>
                    <div className="mc-desc">% of AI conversations resulting in a qualified lead</div>
                  </div>
                </div>
                <div className="metric-card mc-blue">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2l2 5h5l-4 3 1.5 5L9 12l-4.5 3L6 10 2 7h5z" stroke="#1A6FD4" strokeWidth="1.3" strokeLinejoin="round" fill="none"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">Qualification-to-MQL rate</div>
                    <div className="mc-desc">% of qualified leads meeting your MQL criteria</div>
                  </div>
                </div>
                <div className="metric-card mc-blue">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="4" width="12" height="11" rx="2" stroke="#1A6FD4" strokeWidth="1.3" fill="none"/><path d="M3 7h12M6 2v3M12 2v3M6 11l2 2 4-3" stroke="#1A6FD4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">MQL-to-meeting rate</div>
                    <div className="mc-desc">% of MQLs booking a demo inside the conversation</div>
                  </div>
                </div>
                <div className="metric-card mc-blue">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 14l3-5 3 3 3-4 3 2" stroke="#1A6FD4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/><circle cx="15" cy="5" r="2.5" fill="#1A6FD4"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">Meeting-to-opportunity rate</div>
                    <div className="mc-desc">AE conversion from booked meetings to pipeline</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Group 2 — Speed & coverage */}
            <div className="metrics-group">
              <div className="metrics-group-label mg-orange">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="#D97706" strokeWidth="1.3" fill="none"/><path d="M7 4v3l2 1.5" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round"/></svg>
                Speed &amp; coverage
              </div>
              <div className="metrics-row">
                <div className="metric-card mc-orange">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="6.5" stroke="#D97706" strokeWidth="1.3" fill="none"/><path d="M9 5v4l2.5 1.5" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">First response time</div>
                    <div className="mc-desc">Should drop to under 30 seconds with AI</div>
                  </div>
                </div>
                <div className="metric-card mc-orange">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2C5.1 2 2 5.1 2 9s3.1 7 7 7 7-3.1 7-7" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round" fill="none"/><path d="M12 2l2 2-2 2" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 6v3l1.5 1" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">After-hours coverage rate</div>
                    <div className="mc-desc">% of leads engaged outside business hours</div>
                  </div>
                </div>
                <div className="metric-card mc-orange">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2l1.5 4h4l-3.3 2.4 1.3 4L9 10l-3.5 2.4 1.3-4L3.5 6h4z" stroke="#D97706" strokeWidth="1.3" strokeLinejoin="round" fill="none"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">SDR time saved per week</div>
                    <div className="mc-desc">Hours reclaimed from repetitive top-of-funnel work</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Group 3 — Quality */}
            <div className="metrics-group">
              <div className="metrics-group-label mg-green">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="#059669" strokeWidth="1.3" fill="none"/><path d="M4.5 7l2 2 3-3" stroke="#059669" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Quality
              </div>
              <div className="metrics-row">
                <div className="metric-card mc-green">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="3" width="12" height="12" rx="2" stroke="#059669" strokeWidth="1.3" fill="none"/><path d="M6 9l2 2 4-4" stroke="#059669" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">CRM data completeness</div>
                    <div className="mc-desc">% of required fields populated by AI vs manual entry</div>
                  </div>
                </div>
                <div className="metric-card mc-green">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2l2 5h5l-4 3 1.5 5L9 12l-4.5 3L6 10 2 7h5z" stroke="#059669" strokeWidth="1.3" strokeLinejoin="round" fill="none"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">Qualification accuracy</div>
                    <div className="mc-desc">% of AI-qualified leads confirmed as genuine by AEs</div>
                  </div>
                </div>
                <div className="metric-card mc-green">
                  <div className="mc-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 15l3-9 3 5 3-7 3 4" stroke="#059669" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
                  </div>
                  <div className="mc-body">
                    <div className="mc-name">Pipeline contribution</div>
                    <div className="mc-desc">% of total pipeline originated or touched by AI agent</div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="callout callout-insight">
            <div className="callout-label">ROI framework</div>
            <p>
              A simple ROI calculation: (Additional pipeline generated by AI agent × your win rate × ACV) − (Cost of AI platform + setup time). Most teams see positive ROI within 60–90 days of deployment, with pipeline contribution covering platform cost within the first quarter.
            </p>
          </div>

          <hr className="section-divider" />

          {/* WHEN YOU DON'T NEED ONE */}
          <h2>When you do not need an AI inbound SDR agent</h2>

          <p>AI inbound SDR agents are not the right solution for every company. You probably do not need one if:</p>

          <ul>
            <li>Your inbound volume is fewer than 20–30 leads per month (the ROI math doesn't work at low volume)</li>
            <li>Your average deal requires multiple in-person meetings before qualification can happen (AI handles digital-first qualification only)</li>
            <li>Your product requires hands-on demos before any qualification is possible (the agent can't show the product)</li>
            <li>Your team is already responding to every lead within 5 minutes during business hours (the speed advantage is reduced)</li>
            <li>Your ICP is so narrow that you get fewer than 5 qualified leads per month (human outreach is more appropriate at this scale)</li>
          </ul>

          <p>
            The sweet spot: B2B SaaS or services companies with 30+ inbound leads per month, a defined ICP, a demo-based sales motion, and a team that is struggling to keep up with inbound volume while maintaining quality.
          </p>

          <hr className="section-divider" />

          {/* FAQ — matches /ai-support-agent/ design system pattern */}
          <section className="faq-section" aria-label="Frequently asked questions" itemScope itemType="https://schema.org/FAQPage">
            <div className="faq-section-inner">
              <div className="faq-left">
                <span className="faq-tag">FAQs</span>
                <h2 className="faq-heading">Common questions</h2>
                <p className="faq-subtext">AI SDR vs AI BDR, pricing, deployment, integrations, and how it compares to human SDRs.</p>
                <a className="faq-ask-btn" href="/demo/">
                  Ask a question
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>

              <div className="faq-right">
                {[
                  { q: "What is an inbound SDR agent?", a: <p>An inbound SDR agent is an AI-powered teammate that engages, qualifies, and books meetings with leads who reach out via your website or messaging channels. Unlike a traditional chatbot, it adapts to the conversation, captures structured data, and routes ICP-fit leads to your sales team in real time.</p> },
                  { q: "How long does it take to set up an AI inbound SDR agent?", a: <p>With a platform like Dashly, initial setup takes 3&ndash;7 days: configuring the qualification criteria, uploading the knowledge base, connecting the CRM, and setting up meeting booking. Calibration and optimization continue for the first 2&ndash;4 weeks as the agent learns from real interactions.</p> },
                  { q: "What happens when the AI agent cannot answer a question?", a: <p>Well-configured agents have a defined escalation path: they acknowledge the question, let the lead know they are connecting them with a human, and either transfer the conversation live or schedule a callback. They do not guess or fabricate answers.</p> },
                  { q: "Can the AI agent handle multiple conversations at once?", a: <p>Yes &mdash; this is one of the core advantages. Unlike a human SDR, the agent handles unlimited concurrent conversations with no degradation in quality. During a product launch or campaign spike, it scales automatically.</p> },
                  { q: "Does the agent work across channels?", a: <p>Modern AI inbound SDR agents work across website chat, email follow-up, and in some cases WhatsApp or Telegram. The qualification logic and CRM integration are channel-agnostic &mdash; a lead can start in chat and continue via email without losing context.</p> },
                  { q: "How do I measure whether the AI agent is working?", a: <p>Track the funnel metrics listed above: conversation-to-qualification rate, MQL-to-meeting rate, pipeline contribution percentage, and SDR time saved. Compare against your pre-deployment baseline. Most teams see measurable improvements within the first 30 days.</p> },
                  { q: "Will prospects know they're talking to AI?", a: <p>Transparency practices vary. Some companies disclose upfront ("Hi, I am Dashly&rsquo;s AI assistant"). Others configure the agent with a persona name without specifying it is AI. The right choice depends on your brand values and market. What matters most for conversion is that the conversation is useful and relevant &mdash; leads respond positively to agents that help them, regardless of whether they know it is AI.</p> },
                  { q: "What is the difference between AI SDR and AI BDR?", a: <p>The two terms are often used interchangeably, but the functional split matters when picking a vendor: <strong>AI BDR</strong> leans outbound (cold email, LinkedIn prospecting). <strong>AI SDR</strong> spans both inbound (website chat, lead qualification) and outbound. Vendors choose the label that fits their funnel positioning, not a strict industry definition. For a deeper breakdown see the <a href="#what-is-ai-sdr-vs-bdr">disambiguation table above</a>.</p> },
                  { q: "How much does an AI SDR cost in 2026?", a: <p>Pricing ranges from $24/mo (<a href="https://www.dashly.io/tools/ai-sdr-tools/dashly/">Dashly entry plan, inbound</a>) to $900/mo per agent (<a href="https://www.dashly.io/tools/ai-sdr-tools/aisdr/">AISDR outbound</a>) to custom enterprise contracts (Conversica, <a href="https://www.dashly.io/tools/ai-sdr-tools/11x/">11x.ai</a>) at $30k+ ARR. Per-resolution pricing (Intercom Fin) bills around $0.99 per resolved conversation. Free tiers exist for piloting. Total year-one cost typically runs 1.2&ndash;1.5x listed subscription for inbound web agents and 2&ndash;3x for autonomous outbound. See the <a href="#ai-sdr-pricing-models">pricing models table</a>.</p> },
                  { q: "Can an AI SDR work for inbound and outbound at the same time?", a: <p>Most products specialize. <strong>Dashly is inbound-first</strong>; AISDR, 11x.ai, and Salesforge are outbound-first. Hybrid platforms (Salesloft, Outreach) cover both at higher per-seat cost. A single AI agent owning both sides on the same CRM record is the next product wave but is not standard yet in 2026. Pick the side that&rsquo;s the bigger pipeline lever today, then layer the second channel after the first hits target metrics.</p> },
                  { q: "What integrations does an AI SDR need?", a: <p>At minimum: a CRM (HubSpot, Salesforce, Pipedrive), a calendar (Google Calendar, Outlook, Calendly, Chili Piper), and a knowledge base (help center, product docs, sales playbook). Outbound AI SDRs add prospect-data sources (Apollo, ZoomInfo, LinkedIn Sales Navigator) and email-deliverability infrastructure (warm-up tools, sending domains, SPF/DKIM/DMARC alignment).</p> },
                  { q: "What is the best AI SDR for B2B SaaS?", a: <p>For <strong>inbound-heavy B2B SaaS</strong> with active demand-gen and a trial loop, <a href="https://www.dashly.io/tools/ai-sdr-tools/dashly/">Dashly&rsquo;s four-agent inbound stack</a> ranks first by real customer outcome data (568% ROMI at InfluADS, +536% meetings at Advertising Socials). For <strong>outbound-heavy B2B SaaS</strong> entering new geographies, AISDR or 11x.ai cover that side. Most teams need both eventually. Compare all twelve at the <a href="https://www.dashly.io/tools/best-ai-sdr-tools/">12 best AI SDR tools pillar</a>.</p> },
                  { q: "How long does it take to deploy an AI SDR end-to-end?", a: <p>Inbound web-based agents like Dashly deploy in under 1 day to 1 week for the basic setup, with a 30-day pilot-to-scale rollout (Days 1&ndash;7 procurement &amp; setup, Days 8&ndash;21 pilot, Days 22&ndash;30 scale). Autonomous outbound agents (Conversica, 11x.ai) with deep CRM and persona setup take 4 to 8 weeks of procurement plus implementation. The two most common rollout failures: skipping the pilot phase and never enabling CRM write-back. Both are documented in <a href="https://www.dashly.io/blog/how-we-messed-up-the-ai-sdr/">how teams mess up AI SDR rollouts</a>.</p> },
                ].map((item, i) => {
                  const isOpen = openFaq === i;
                  return (
                    <div
                      key={i}
                      className={`faq-item ${isOpen ? "faq-item-open" : ""}`}
                      itemScope
                      itemProp="mainEntity"
                      itemType="https://schema.org/Question"
                    >
                      <button
                        className="faq-item-btn"
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                        aria-expanded={isOpen}
                      >
                        <span className="faq-item-q" itemProp="name">{item.q}</span>
                        <span className="faq-item-arrow" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      </button>
                      <div
                        className="faq-item-body"
                        itemScope
                        itemProp="acceptedAnswer"
                        itemType="https://schema.org/Answer"
                      >
                        <div className="faq-item-answer" itemProp="text">{item.a}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="article-cta">
            <h2>See an AI SDR agent <em>in action</em></h2>
            <p>Book a 30-minute demo and we will show you how Dashly&rsquo;s AI SDR agent works on your inbound traffic &mdash; with your ICP criteria, your qualification framework, and your CRM.</p>
            <div className="cta-buttons">
              <a href="/demo/" className="btn-primary">Book a demo →</a>
              <a href="/ai-qualifier-agent/" className="btn-ghost">See the landing page</a>
            </div>
          </div>

        </div>
      </main>

    </Layout>
  );
}
